import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  clearAuth,
  createUserPositionAdmin,
  getAllUsers,
  getUserHistoryAdmin,
  getUserPositionsAdmin,
  getBalanceRequestsAdmin,
  decideBalanceRequestAdmin,
  updateUserPositionAdmin,
  closeUserPositionAdmin,
  exitAllPositionsAdmin,
  exitAllPositionsGlobal,
  toggleUserBotAdmin,
  adminCreateUser,
  adminDeleteUser,
  adminResetUserPassword,
  updateUserTradeSettingsAdmin,
  getOptionExpiriesAdmin,
  getOptionStrikesAdmin,
  getOptionQuoteAdmin,
  applyManualSwapAdmin,
  openManualPositionAdmin,
  downloadLedgerCsvAdmin,
  getGlobalBotStatus,
  pauseAllBotEntries,
  resumeAllBotEntries,
  resolveWsUrl,
  getToken,
  API_BASE_URL,
  initiateAdminWithdrawal,
  getAdminWithdrawalRequests,
  cancelAdminWithdrawal,
  getUserBotsAdmin,
  updateUserBotAdmin,
  startUserBotAdmin,
  stopUserBotAdmin,
  stopAllUserBotsAdmin,
  downloadUserTransactionReport,
  executeSpxOptionsTrade,
  updateSpxMultipliersBulk,
} from "../api";
import { ThemeContext } from "../App";
import ManualGoldEntry from "../components/ManualGoldEntry";

// Feature flag: the legacy options manual-entry cards (Buy Option Position + S&P500
// Options Trade) are hidden per product decision. Set to true to restore them.
const SHOW_LEGACY_OPTION_CARDS = false;

const REFRESH_INTERVAL_MS = 30_000; // Increased from 10s to 30s to reduce API pressure

// XAUUSD lot multiplier: 0.01 lot = 1 oz of gold
const XAUUSD_LOT_MULTIPLIER = 100;

const getLotMultiplier = (symbol) => {
  const normalized = (symbol || "").toUpperCase().replace(/[/_]/g, "");
  if (normalized === "XAUUSD") return XAUUSD_LOT_MULTIPLIER;
  return 1; // Crypto uses direct qty
};

const CRYPTO_CONFIGS = [
  { symbol: "BTCUSD", label: "Bitcoin (BTC)", icon: "₿", defaultQty: 0.001 },
  { symbol: "ETHUSD", label: "Ethereum (ETH)", icon: "Ξ", defaultQty: 0.01 },
  { symbol: "SOLUSD", label: "Solana (SOL)", icon: "◎", defaultQty: 0.1 },
  { symbol: "BNBUSD", label: "Binance Coin (BNB)", icon: "◆", defaultQty: 0.01 },
  { symbol: "XAUUSD", label: "Gold (XAU)", icon: "🥇", defaultQty: 0.01 },
  { symbol: "SPX500USD", label: "S&P 500 Index", icon: "📈", defaultQty: 0.1 },
];

function BotControlPanel({
  userId,
  userBots,
  botConfigEdits,
  setBotConfigEdits,
  botUpdating,
  onToggle,
  onConfigSave,
  onStopAll,
  onManualPosition,
  manualPositionLoading,
  styles,
}) {
  const activeBotCount = CRYPTO_CONFIGS.filter(
    (c) => userBots?.[c.symbol]?.is_active
  ).length;

  // State for manual position entry per symbol
  const [expandedManual, setExpandedManual] = React.useState({});
  const [manualPositions, setManualPositions] = React.useState({});

  const handleEditChange = (symbol, field, value) => {
    const key = `${userId}-${symbol}`;
    setBotConfigEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  // Check if any field has been edited for a symbol
  const hasEdits = (symbol) => {
    const editKey = `${userId}-${symbol}`;
    const edits = botConfigEdits[editKey] || {};
    return (
      edits.default_qty !== undefined ||
      edits.grid_spacing_percent !== undefined ||
      edits.take_profit_percent !== undefined ||
      edits.slippage_points !== undefined ||
      edits.max_grid !== undefined ||
      edits.range_lower !== undefined ||
      edits.range_upper !== undefined
    );
  };

  return (
    <div className={`p-4 rounded-lg ${styles.secondaryCard}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Trading Bots</h3>
          <p className={`text-xs ${styles.muted}`}>{activeBotCount}/{CRYPTO_CONFIGS.length} active</p>
        </div>
        {activeBotCount > 0 && (
          <button
            className="px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-sm"
            onClick={() => onStopAll(userId)}
            disabled={botUpdating?.userId === userId}
          >
            Stop All Bots
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CRYPTO_CONFIGS.map((crypto) => {
          const botConfig = userBots?.[crypto.symbol] || {};
          const isActive = botConfig.is_active || false;
          const editKey = `${userId}-${crypto.symbol}`;
          const edits = botConfigEdits[editKey] || {};
          const isUpdating =
            botUpdating?.userId === userId &&
            (botUpdating?.symbol === crypto.symbol ||
              botUpdating?.symbol === "ALL");

          return (
            <div
              key={crypto.symbol}
              className={`p-4 rounded-lg border-2 ${
                isActive
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-gray-600 bg-gray-800/50"
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{crypto.icon}</span>
                  <div>
                    <p className="font-semibold">{crypto.symbol}</p>
                    <p className={`text-xs ${styles.muted}`}>{crypto.label}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    isActive
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-600 text-gray-300"
                  }`}
                >
                  {isActive ? "● Running" : "○ Stopped"}
                </span>
              </div>

              {/* Quantity Input */}
              <div className="mb-2">
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  Trade Quantity
                </label>
                <input
                  type="number"
                  step="0.0001"
                  className={`${styles.input} w-full`}
                  value={
                    edits.default_qty !== undefined
                      ? edits.default_qty
                      : botConfig.default_qty ?? crypto.defaultQty
                  }
                  onChange={(e) =>
                    handleEditChange(
                      crypto.symbol,
                      "default_qty",
                      e.target.value
                    )
                  }
                  disabled={isUpdating}
                />
                <p className={`text-[10px] mt-0.5 ${styles.muted}`}>
                  Default: {crypto.defaultQty}
                </p>
              </div>

              {/* Grid Settings */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className={`text-xs ${styles.muted} block mb-1`}>
                    Grid %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className={`${styles.input} w-full`}
                    value={
                      edits.grid_spacing_percent !== undefined
                        ? edits.grid_spacing_percent
                        : botConfig.grid_spacing_percent ?? ""
                    }
                    placeholder="0.5"
                    onChange={(e) =>
                      handleEditChange(
                        crypto.symbol,
                        "grid_spacing_percent",
                        e.target.value
                      )
                    }
                    disabled={isUpdating}
                  />
                </div>
                <div>
                  <label className={`text-xs ${styles.muted} block mb-1`}>
                    TP %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className={`${styles.input} w-full`}
                    value={
                      edits.take_profit_percent !== undefined
                        ? edits.take_profit_percent
                        : botConfig.take_profit_percent ?? ""
                    }
                    placeholder="0.3"
                    onChange={(e) =>
                      handleEditChange(
                        crypto.symbol,
                        "take_profit_percent",
                        e.target.value
                      )
                    }
                    disabled={isUpdating}
                  />
                </div>
              </div>

              {/* Slippage */}
              <div className="mb-3">
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  Slippage (points)
                </label>
                <input
                  type="number"
                  step="0.1"
                  className={`${styles.input} w-full`}
                  value={
                    edits.slippage_points !== undefined
                      ? edits.slippage_points
                      : botConfig.slippage_points ?? ""
                  }
                  placeholder={botConfig.default_slippage_points ?? "0"}
                  onChange={(e) =>
                    handleEditChange(
                      crypto.symbol,
                      "slippage_points",
                      e.target.value
                    )
                  }
                  disabled={isUpdating}
                />
                <p className={`text-[10px] mt-0.5 ${styles.muted}`}>
                  Default: {botConfig.default_slippage_points ?? "0"} pts
                </p>
              </div>

              {/* Max Grid */}
              <div className="mb-3">
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  Max Grid
                </label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  className={`${styles.input} w-full`}
                  value={
                    edits.max_grid !== undefined
                      ? edits.max_grid
                      : botConfig.max_grid ?? ""
                  }
                  placeholder={botConfig.default_max_grid ?? "25"}
                  onChange={(e) =>
                    handleEditChange(
                      crypto.symbol,
                      "max_grid",
                      e.target.value
                    )
                  }
                  disabled={isUpdating}
                />
                <p className={`text-[10px] mt-0.5 ${styles.muted}`}>
                  Default: {botConfig.default_max_grid ?? "25"} (excludes manual)
                </p>
              </div>

              {/* Trading Range - Feature 2 */}
              <div className="mb-3">
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  Trading Range (Price Limits)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.input} w-full text-sm`}
                      value={
                        edits.range_lower !== undefined
                          ? edits.range_lower
                          : botConfig.range_lower ?? ""
                      }
                      placeholder="Lower"
                      onChange={(e) =>
                        handleEditChange(
                          crypto.symbol,
                          "range_lower",
                          e.target.value
                        )
                      }
                      disabled={isUpdating}
                    />
                    <p className={`text-[9px] ${styles.muted}`}>Min price</p>
                  </div>
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.input} w-full text-sm`}
                      value={
                        edits.range_upper !== undefined
                          ? edits.range_upper
                          : botConfig.range_upper ?? ""
                      }
                      placeholder="Upper"
                      onChange={(e) =>
                        handleEditChange(
                          crypto.symbol,
                          "range_upper",
                          e.target.value
                        )
                      }
                      disabled={isUpdating}
                    />
                    <p className={`text-[9px] ${styles.muted}`}>Max price</p>
                  </div>
                </div>
                <p className={`text-[10px] mt-0.5 ${styles.muted}`}>
                  Bot only opens trades within this range. Leave empty for no limit.
                </p>
              </div>

              {/* Save Settings Button */}
              {hasEdits(crypto.symbol) && (
                <button
                  className="w-full py-1.5 mb-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
                  onClick={() => onConfigSave(userId, crypto.symbol)}
                  disabled={isUpdating}
                >
                  {isUpdating ? "Saving..." : "💾 Save Settings"}
                </button>
              )}

              {/* Start/Stop Button */}
              <button
                className={`w-full py-2 rounded font-semibold text-white ${
                  isUpdating
                    ? "bg-gray-500 cursor-not-allowed"
                    : isActive
                    ? "bg-red-600 hover:bg-red-500"
                    : "bg-emerald-600 hover:bg-emerald-500"
                }`}
                onClick={() => onToggle(userId, crypto.symbol, !isActive)}
                disabled={isUpdating}
              >
                {isUpdating
                  ? "Updating..."
                  : isActive
                  ? "Stop Bot"
                  : "Start Bot"}
              </button>

              {/* Manual Position Entry Toggle - Feature 4: Works with or without bot running */}
              <button
                className={`w-full mt-2 py-1.5 rounded text-sm border ${
                  "border-purple-500 text-purple-400 hover:bg-purple-500/20"
                }`}
                onClick={() => {
                  setExpandedManual((prev) => ({
                    ...prev,
                    [crypto.symbol]: !prev[crypto.symbol],
                  }));
                }}
              >
                {expandedManual[crypto.symbol]
                  ? "▲ Hide Manual Entry"
                  : `▼ Manual Position Entry ${!isActive ? "(Bot Off)" : ""}`}
              </button>

              {/* Collapsible Manual Position Form */}
              {expandedManual[crypto.symbol] && (
                <div className="mt-3 p-3 rounded bg-gray-800/50 border border-gray-600">
                  <p className={`text-xs mb-2 ${styles.muted}`}>
                    Manual {crypto.symbol} Entry
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className={`text-[10px] ${styles.muted}`}>
                        Side
                      </label>
                      <select
                        className={`${styles.input} text-sm`}
                        value={manualPositions[crypto.symbol]?.side || "buy"}
                        onChange={(e) =>
                          setManualPositions((prev) => ({
                            ...prev,
                            [crypto.symbol]: {
                              ...(prev[crypto.symbol] || {}),
                              side: e.target.value,
                            },
                          }))
                        }
                        disabled={manualPositionLoading === crypto.symbol}
                      >
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                      </select>
                    </div>
                    <div>
                      <label className={`text-[10px] ${styles.muted}`}>
                        TP Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className={`${styles.input} text-sm`}
                        placeholder="Take Profit"
                        value={manualPositions[crypto.symbol]?.takeProfit || ""}
                        onChange={(e) =>
                          setManualPositions((prev) => ({
                            ...prev,
                            [crypto.symbol]: {
                              ...(prev[crypto.symbol] || {}),
                              takeProfit: e.target.value,
                            },
                          }))
                        }
                        disabled={manualPositionLoading === crypto.symbol}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] ${styles.muted}`}>
                        Quantity
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        className={`${styles.input} text-sm`}
                        placeholder={String(crypto.defaultQty)}
                        value={manualPositions[crypto.symbol]?.qty || ""}
                        onChange={(e) =>
                          setManualPositions((prev) => ({
                            ...prev,
                            [crypto.symbol]: {
                              ...(prev[crypto.symbol] || {}),
                              qty: e.target.value,
                            },
                          }))
                        }
                        disabled={manualPositionLoading === crypto.symbol}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] ${styles.muted}`}>
                        Notes
                      </label>
                      <input
                        type="text"
                        className={`${styles.input} text-sm`}
                        placeholder="Optional"
                        value={manualPositions[crypto.symbol]?.notes || ""}
                        onChange={(e) =>
                          setManualPositions((prev) => ({
                            ...prev,
                            [crypto.symbol]: {
                              ...(prev[crypto.symbol] || {}),
                              notes: e.target.value,
                            },
                          }))
                        }
                        disabled={manualPositionLoading === crypto.symbol}
                      />
                    </div>
                    {/* Feature 4: Entry Price (optional) */}
                    <div>
                      <label className={`text-[10px] ${styles.muted}`}>
                        Entry Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className={`${styles.input} text-sm`}
                        placeholder="Market"
                        value={manualPositions[crypto.symbol]?.entryPrice || ""}
                        onChange={(e) =>
                          setManualPositions((prev) => ({
                            ...prev,
                            [crypto.symbol]: {
                              ...(prev[crypto.symbol] || {}),
                              entryPrice: e.target.value,
                            },
                          }))
                        }
                        disabled={manualPositionLoading === crypto.symbol}
                      />
                    </div>
                  </div>
                  {/* Feature 4: Note when bot is not running */}
                  {!isActive && (
                    <p className="text-[10px] text-yellow-400 mb-2">
                      ⚠ Bot is not running. Position will be monitored by TP service.
                    </p>
                  )}
                  <button
                    className={`w-full py-1.5 rounded text-white text-sm font-medium ${
                      manualPositionLoading === crypto.symbol
                        ? "bg-purple-400"
                        : "bg-purple-600 hover:bg-purple-500"
                    }`}
                    onClick={() => {
                      const pos = manualPositions[crypto.symbol] || {};
                      onManualPosition(
                        crypto.symbol,
                        pos.side || "buy",
                        pos.takeProfit,
                        pos.qty,
                        pos.notes,
                        pos.entryPrice  // Feature 4: Pass entry price
                      );
                      setManualPositions((prev) => ({
                        ...prev,
                        [crypto.symbol]: {
                          side: "buy",
                          takeProfit: "",
                          qty: "",
                          notes: "",
                          entryPrice: "",  // Feature 4: Clear entry price after opening
                        },
                      }));
                    }}
                    disabled={
                      manualPositionLoading === crypto.symbol ||
                      !manualPositions[crypto.symbol]?.takeProfit ||
                      !manualPositions[crypto.symbol]?.qty
                    }
                  >
                    {manualPositionLoading === crypto.symbol
                      ? "Opening..."
                      : `Open ${crypto.symbol} Position`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ManualPositionEntry({ selectedUser, styles, setStatus }) {
  const [manualPosition, setManualPosition] = useState({
    side: "buy",
    takeProfit: "",
    qty: "",
    notes: "",
  });
  const [openingPosition, setOpeningPosition] = useState(false);

  const handleOpenManualPosition = async () => {
    if (!selectedUser) return;
    const tp = parseFloat(manualPosition.takeProfit);
    const qty = parseFloat(manualPosition.qty);
    if (!Number.isFinite(tp) || tp <= 0) {
      setStatus({ type: "error", message: "Enter a valid take profit price." });
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setStatus({
        type: "error",
        message: "Enter a valid quantity greater than zero.",
      });
      return;
    }
    setOpeningPosition(true);
    try {
      const res = await openManualPositionAdmin(
        selectedUser.user_id,
        manualPosition.side,
        tp,
        qty,
        manualPosition.notes.trim() || null
      );
      setStatus({
        type: "success",
        message:
          res.message || `Manual ${manualPosition.side} position opened.`,
      });
      setManualPosition({ side: "buy", takeProfit: "", qty: "", notes: "" });
    } catch (err) {
      console.error("Failed to open manual position", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail ||
          err.message ||
          "Unable to open manual position.",
      });
    } finally {
      setOpeningPosition(false);
    }
  };

  return (
    <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
      <p className={`text-xs ${styles.muted}`}>Manual Position Entry</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        <div>
          <label className={`text-[11px] ${styles.muted}`}>Side</label>
          <select
            className={styles.input}
            value={manualPosition.side}
            onChange={(e) =>
              setManualPosition((prev) => ({ ...prev, side: e.target.value }))
            }
            disabled={openingPosition}
          >
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
        </div>
        <div>
          <label className={`text-[11px] ${styles.muted}`}>
            Take Profit Price
          </label>
          <input
            className={styles.input}
            type="number"
            step="0.01"
            min="0"
            placeholder="3250.00"
            value={manualPosition.takeProfit}
            onChange={(e) =>
              setManualPosition((prev) => ({
                ...prev,
                takeProfit: e.target.value,
              }))
            }
            disabled={openingPosition}
          />
        </div>
        <div>
          <label className={`text-[11px] ${styles.muted}`}>
            Quantity (lots)
          </label>
          <input
            className={styles.input}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.01"
            value={manualPosition.qty}
            onChange={(e) =>
              setManualPosition((prev) => ({ ...prev, qty: e.target.value }))
            }
            disabled={openingPosition}
          />
        </div>
        <div>
          <label className={`text-[11px] ${styles.muted}`}>
            Notes (optional)
          </label>
          <input
            className={styles.input}
            type="text"
            placeholder="Reason for manual entry"
            value={manualPosition.notes}
            onChange={(e) =>
              setManualPosition((prev) => ({ ...prev, notes: e.target.value }))
            }
            disabled={openingPosition}
          />
        </div>
      </div>
      <p className={`text-[11px] mt-2 ${styles.muted}`}>
        Opens a position at current market price with the specified TP. Entry
        price determined by live quote.
      </p>
      <div className="flex gap-2 mt-3">
        <button
          className={`px-3 py-1 rounded text-white text-sm ${
            openingPosition
              ? "bg-purple-400"
              : "bg-purple-600 hover:bg-purple-500"
          }`}
          onClick={handleOpenManualPosition}
          disabled={
            openingPosition || !manualPosition.takeProfit || !manualPosition.qty
          }
        >
          {openingPosition ? "Opening..." : "Open Manual Position"}
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [positionsByUser, setPositionsByUser] = useState({});
  const [positionEdits, setPositionEdits] = useState({});
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [closingPositionId, setClosingPositionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [balanceRequests, setBalanceRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [botUpdating, setBotUpdating] = useState(null);
  const manageSectionRef = useRef(null);
  const positionsSocketRef = useRef(null);
  const heartbeatRef = useRef(null);
  // Control when we auto-scroll to the manage section
  const [scrollOnManage, setScrollOnManage] = useState(false);
  const navigate = useNavigate();
  const [creatingOption, setCreatingOption] = useState(false);
  const [newOption, setNewOption] = useState({
    symbol: "GLD",
    qty: "1",
    premium: "",
    current: "",
    strike: "",
    expiry: "",
    optionType: "put",
  });
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [creatingUser, setCreatingUser] = useState(false);
  // Manual password set - fallback while password-reset emails are unavailable
  const [pwdReset, setPwdReset] = useState({
    userId: "",
    password: "",
    confirm: "",
    forceLogout: false,
  });
  const [pwdResetShow, setPwdResetShow] = useState(false);
  const [pwdResetSaving, setPwdResetSaving] = useState(false);
  const [pwdResetError, setPwdResetError] = useState("");
  const [pwdResetDone, setPwdResetDone] = useState(null);
  const [pwdResetCopied, setPwdResetCopied] = useState(false);
  const [tradeQtyDraft, setTradeQtyDraft] = useState("");
  const [savingTradeQty, setSavingTradeQty] = useState(false);
  const [tradeQtyError, setTradeQtyError] = useState("");
  const [optionExpiries, setOptionExpiries] = useState([]);
  const [loadingOptionExpiries, setLoadingOptionExpiries] = useState(false);
  const [, setOptionStrikes] = useState([]);
  const [loadingOptionStrikes, setLoadingOptionStrikes] = useState(false);
  const [optionPrice, setOptionPrice] = useState(null);
  const [loadingOptionPrice, setLoadingOptionPrice] = useState(false);
  const [optionSpot, setOptionSpot] = useState(null);
  const [premiumEdited, setPremiumEdited] = useState(false);
  const [currentEdited, setCurrentEdited] = useState(false);
  const [exitAllLoading, setExitAllLoading] = useState(false);
  const [exitAllSlippage, setExitAllSlippage] = useState("0");
  const [sysHealth, setSysHealth] = useState(null);
  const [sysHealthLoading, setSysHealthLoading] = useState(false);
  const [pnlRange, setPnlRange] = useState("7d");
  const [pnlCustom, setPnlCustom] = useState({ start: "", end: "" });
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlByUser, setPnlByUser] = useState({});
  const [pnlSendStatus, setPnlSendStatus] = useState(null);
  const [ledgerUserId, setLedgerUserId] = useState("");
  const [ledgerDateRange, setLedgerDateRange] = useState("all");
  const [ledgerCustomDates, setLedgerCustomDates] = useState({
    start: "",
    end: "",
  });
  const [ledgerDownloading, setLedgerDownloading] = useState(false);
  const [manualSwap, setManualSwap] = useState({
    amount: "",
    startDate: "",
    endDate: "",
    notes: "",
  });
  const [applyingSwap, setApplyingSwap] = useState(false);
  const [globalBotStatus, setGlobalBotStatus] = useState(null);
  const [globalBotLoading, setGlobalBotLoading] = useState(false);
  const [exitAllGlobalLoading, setExitAllGlobalLoading] = useState(false);
  const [adminWithdrawalAmount, setAdminWithdrawalAmount] = useState("");
  const [adminWithdrawalNote, setAdminWithdrawalNote] = useState("");
  const [initiatingWithdrawal, setInitiatingWithdrawal] = useState(false);
  const [adminWithdrawalRequests, setAdminWithdrawalRequests] = useState([]);
  const [loadingWithdrawalRequests, setLoadingWithdrawalRequests] =
    useState(false);
  const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState(null);
  // Multi-bot management state
  const [userBots, setUserBots] = useState({}); // { userId: { BTCUSD: {...}, ETHUSD: {...}, SOLUSD: {...} } }
  const [multiBotUpdating, setMultiBotUpdating] = useState(null); // { userId, symbol } or null
  const [botConfigEdits, setBotConfigEdits] = useState({}); // { `${userId}-${symbol}`: { default_qty: "0.001" } }
  const [manualPositionLoading, setManualPositionLoading] = useState(null); // symbol or null
  const [systemHealthCollapsed, setSystemHealthCollapsed] = useState(true); // collapsed by default
  const [directBalanceAmount, setDirectBalanceAmount] = useState("");
  const [directBalanceNote, setDirectBalanceNote] = useState("");
  const [addingDirectBalance, setAddingDirectBalance] = useState(false);
  // Transaction Report state
  const [reportPeriod, setReportPeriod] = useState("7days");
  const [reportCustomDates, setReportCustomDates] = useState({ start: "", end: "" });
  const [reportDownloading, setReportDownloading] = useState(false);
  // Performance fix: Pause polling during save/update operations
  const [isPollingPaused, setIsPollingPaused] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  // ── SPX Options Panel State ──
  const [spxLegs, setSpxLegs] = useState([
    { enabled: true,  option_type: "CE", side: "buy",  strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
    { enabled: true,  option_type: "CE", side: "sell", strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
    { enabled: false, option_type: "PE", side: "buy",  strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
    { enabled: false, option_type: "PE", side: "sell", strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
  ]);
  const [spxTradeDate, setSpxTradeDate] = useState(new Date().toISOString().slice(0, 10));
  const [spxExpiryDate, setSpxExpiryDate] = useState("");
  const [spxNotes, setSpxNotes] = useState("");
  const [spxPreview, setSpxPreview] = useState(null);
  const [spxResult, setSpxResult] = useState(null);
  const [spxLoading, setSpxLoading] = useState(false);
  const [spxPanelOpen, setSpxPanelOpen] = useState(false);
  const [spxApplyAll, setSpxApplyAll] = useState(false);
  const [spxMultipliers, setSpxMultipliers] = useState({});
  const [spxMultipliersOpen, setSpxMultipliersOpen] = useState(false);
  const [savingMultipliers, setSavingMultipliers] = useState(false);

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate("/");
  }, [navigate]);

  const fetchGlobalBotStatus = useCallback(async () => {
    try {
      const data = await getGlobalBotStatus();
      setGlobalBotStatus(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Failed to fetch global bot status", err);
    }
  }, [handleLogout]);

  const handleToggleGlobalEntries = useCallback(async () => {
    if (!globalBotStatus) return;
    setGlobalBotLoading(true);
    try {
      if (globalBotStatus.entries_paused) {
        await resumeAllBotEntries();
        setStatus({
          type: "success",
          message: "All bot entries resumed. Bots can now take new trades.",
        });
      } else {
        await pauseAllBotEntries();
        setStatus({
          type: "success",
          message:
            "All bot entries paused. Existing positions will continue to TP.",
        });
      }
      await fetchGlobalBotStatus();
    } catch (err) {
      console.error("Failed to toggle global bot entries", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail || "Unable to toggle global bot entries.",
      });
    } finally {
      setGlobalBotLoading(false);
    }
  }, [globalBotStatus, fetchGlobalBotStatus]);

  useEffect(() => {
    fetchGlobalBotStatus();
    if (isPollingPaused) return undefined; // Don't poll during operations
    const interval = setInterval(fetchGlobalBotStatus, 30000); // Increased from 10s to 30s
    return () => clearInterval(interval);
  }, [fetchGlobalBotStatus, isPollingPaused]);

  const fetchSystemHealth = useCallback(async () => {
    setSysHealthLoading(true);
    try {
      const [status, system] = await Promise.all([
        (await import("../api")).getAdminHealthStatus(),
        (await import("../api")).getAdminSystemHealth(),
      ]);
      setSysHealth({ status, system });
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      if (err?.response?.status === 403) {
        setSysHealth({
          status: { status: "forbidden" },
          system: {
            text: "Not authorized to view system health. Login as admin.",
          },
        });
        return;
      }
      console.error("Failed to fetch system health", err);
      setSysHealth({
        status: { status: "error" },
        system: {
          text: "Failed to fetch system health. Check backend logs/network.",
        },
      });
    } finally {
      setSysHealthLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchSystemHealth();
    if (isPollingPaused) return undefined; // Don't poll during operations
    const id = setInterval(fetchSystemHealth, 30000); // Increased from 10s to 30s
    return () => clearInterval(id);
  }, [fetchSystemHealth, isPollingPaused]);

  const mapPositionToEdits = useCallback((position) => {
    // Convert qty to display format (lot size) for XAUUSD
    const rawQty = position.qty;
    const multiplier = getLotMultiplier(position.symbol);
    const displayQty = rawQty != null ? rawQty / multiplier : rawQty;

    return {
      symbol: position.symbol,
      side: position.side,
      qty: displayQty,
      entry_price: position.entry_price,
      current_price: position.current_price,
      is_option: position.is_option,
      option_type: position.is_option ? position.option_type || "call" : "",
      strike_price:
        position.is_option && position.strike_price !== null
          ? position.strike_price
          : "",
      expiry_date:
        position.is_option && position.expiry_date ? position.expiry_date : "",
      option_premium:
        position.is_option && position.option_premium !== null
          ? position.option_premium
          : position.entry_price,
      status: position.status || "open",
      closed_price:
        position.closed_price !== null && position.closed_price !== undefined
          ? position.closed_price
          : "",
    };
  }, []);

  const updateUserAggregates = useCallback(
    (userId, updatedPositions) => {
      setUsers((prev) =>
        prev.map((user) => {
          if (user.user_id !== userId) return user;
          const totalPnl = updatedPositions.reduce((acc, pos) => {
            const pnl = Number(pos.unrealized_pnl ?? 0);
            return acc + (Number.isFinite(pnl) ? pnl : 0);
          }, 0);
          return {
            ...user,
            open_positions: updatedPositions.length,
            total_unrealized_pnl: Number(totalPnl.toFixed(2)),
          };
        })
      );
    },
    [setUsers]
  );

  const positionsWsUrl = useMemo(() => {
    const tokenValue = getToken?.();
    if (!tokenValue) return null;
    try {
      const url = new URL(resolveWsUrl("/ws/positions"));
      url.searchParams.set("token", tokenValue);
      return url.toString();
    } catch (error) {
      console.error("Failed to resolve admin positions websocket URL", error);
      return null;
    }
  }, []);

  const styles = useMemo(() => {
    const isDark = theme === "dark";
    const base = isDark
      ? "bg-slate-900 text-white"
      : "bg-slate-50 text-gray-900";
    const card = isDark
      ? "bg-gray-800 text-white border border-gray-700"
      : "bg-white text-gray-900 border border-gray-200";
    const secondaryCard = isDark
      ? "bg-gray-900 border border-gray-700 text-white"
      : "bg-white border border-gray-200 text-gray-900";
    const tableBorder = isDark ? "border-gray-700" : "border-gray-300";
    const tableClass = `min-w-full border-collapse border ${tableBorder} text-inherit`;
    const cellClass = `border ${tableBorder} px-3 py-2`;
    const headerRow = isDark ? "bg-gray-800" : "bg-gray-100";
    const input = isDark
      ? "bg-gray-900 border border-gray-600 rounded px-2 py-1 w-full text-white placeholder-gray-400"
      : "bg-white border border-gray-300 rounded px-2 py-1 w-full text-gray-900 placeholder-gray-500";
    const optionPill = isDark
      ? "text-xs bg-yellow-500/80 text-gray-900 px-2 py-0.5 rounded"
      : "text-xs bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded";
    const statusSuccess = isDark
      ? "bg-green-700 text-white"
      : "bg-green-100 text-green-800";
    const statusError = isDark
      ? "bg-red-700 text-white"
      : "bg-red-100 text-red-800";
    const borderMuted = isDark ? "border-gray-700" : "border-gray-200";
    return {
      base,
      card,
      secondaryCard,
      tableClass,
      cellClass,
      headerRow,
      input,
      optionPill,
      muted: isDark ? "text-gray-400" : "text-gray-500",
      statusSuccess,
      statusError,
      borderMuted,
      mono: isDark
        ? "font-mono text-xs text-gray-300"
        : "font-mono text-xs text-gray-700",
    };
  }, [theme]);

  const selectedUser = useMemo(
    () => users.find((user) => user.user_id === selectedUserId) || null,
    [selectedUserId, users]
  );

  const selectedPositions = useMemo(
    () => positionsByUser[selectedUserId] || [],
    [positionsByUser, selectedUserId]
  );

  const hasSpotCryptoPositions = useMemo(
    () =>
      selectedPositions.some((position) => {
        if (!position || position.is_option) return false;
        const symbol = String(position.symbol || "")
          .replace(/[/_]/g, "")
          .toUpperCase();
        return ["BTCUSD", "ETHUSD", "SOLUSD"].includes(symbol);
      }),
    [selectedPositions]
  );

  const parseNumeric = useCallback((value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }, []);

  const formatQuantity = useCallback(
    (value) => {
      const numeric = parseNumeric(value);
      if (numeric === null) return "--";
      // For values >= 1, show max 2 decimals
      // For small values (0.01+), show up to 4 decimals
      // For very small values (< 0.01 like 0.001), show up to 6 decimals
      let options;
      if (numeric >= 1) {
        options = { maximumFractionDigits: 2 };
      } else if (numeric >= 0.01) {
        options = { minimumFractionDigits: 2, maximumFractionDigits: 4 };
      } else {
        options = { minimumFractionDigits: 3, maximumFractionDigits: 6 };
      }
      return numeric.toLocaleString(undefined, options);
    },
    [parseNumeric]
  );

  const fetchOptionExpiries = useCallback(
    async (_type) => {
      setLoadingOptionExpiries(true);
      try {
        const res = await getOptionExpiriesAdmin();
        const expiries = Array.isArray(res?.expiries) ? res.expiries : [];
        setOptionExpiries(expiries);
        setOptionStrikes([]);
        setOptionSpot(null);
        setOptionPrice(null);
      } catch (err) {
        console.error("Failed to fetch option expiries", err);
        if (err?.response?.status === 401) {
          handleLogout();
          return;
        }
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail || "Unable to load option expiries.",
        });
        setOptionSpot(null);
      } finally {
        setLoadingOptionExpiries(false);
      }
    },
    [setStatus, handleLogout]
  );

  const fetchOptionStrikes = useCallback(
    async (expiry, type) => {
      if (!expiry) {
        setOptionStrikes([]);
        setOptionSpot(null);
        return;
      }
      setLoadingOptionStrikes(true);
      try {
        const res = await getOptionStrikesAdmin(expiry, type);
        const strikes = Array.isArray(res?.strikes) ? res.strikes : [];
        setOptionStrikes(strikes);
        setOptionSpot(res?.spot ?? null);
      } catch (err) {
        console.error("Failed to fetch option strikes", err);
        if (err?.response?.status === 401) {
          handleLogout();
          return;
        }
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail || "Unable to load option strikes.",
        });
        setOptionStrikes([]);
        setOptionSpot(null);
        setOptionPrice(null);
      } finally {
        setLoadingOptionStrikes(false);
      }
    },
    [setStatus, handleLogout]
  );

  const fetchOptionPrice = useCallback(
    async (expiry, strike, type) => {
      if (!expiry || !strike) {
        setOptionPrice(null);
        return;
      }
      setLoadingOptionPrice(true);
      try {
        const res = await getOptionQuoteAdmin(expiry, strike, type);
        const price = res?.price;
        if (Number.isFinite(price)) {
          const normalized = Number(price).toFixed(2);
          setOptionPrice(price);
          setNewOption((prev) => {
            const nextPremium = premiumEdited ? prev.premium : normalized;
            const nextCurrent = currentEdited ? prev.current : normalized;
            if (nextPremium === prev.premium && nextCurrent === prev.current) {
              return prev;
            }
            return {
              ...prev,
              premium: nextPremium,
              current: nextCurrent,
            };
          });
        } else {
          setOptionPrice(null);
        }
      } catch (err) {
        console.error("Failed to fetch option price", err);
        if (err?.response?.status === 401) {
          handleLogout();
          return;
        }
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail || "Unable to fetch option price.",
        });
        setOptionPrice(null);
      } finally {
        setLoadingOptionPrice(false);
      }
    },
    [setStatus, handleLogout, premiumEdited, currentEdited]
  );

  const currentExpiry = newOption.expiry;

  useEffect(() => {
    if (!selectedUserId) return;

    if (!optionExpiries.length) {
      setNewOption((prev) => {
        if (prev.expiry === "") {
          return prev;
        }
        return {
          ...prev,
          expiry: "",
          strike: "",
          ...(premiumEdited ? {} : { premium: "" }),
          ...(currentEdited ? {} : { current: "" }),
        };
      });
      setPremiumEdited(false);
      setCurrentEdited(false);
      return;
    }

    if (currentExpiry && optionExpiries.includes(currentExpiry)) {
      return;
    }

    const nextExpiry = optionExpiries[0] || "";
    setNewOption((prev) => {
      if (prev.expiry === nextExpiry) {
        return prev;
      }
      return {
        ...prev,
        expiry: nextExpiry,
        strike: "",
        ...(premiumEdited ? {} : { premium: "" }),
        ...(currentEdited ? {} : { current: "" }),
      };
    });
    setPremiumEdited(false);
    setCurrentEdited(false);
  }, [
    optionExpiries,
    currentExpiry,
    premiumEdited,
    currentEdited,
    selectedUserId,
  ]);

  useEffect(() => {
    if (selectedUser) {
      const value = parseNumeric(selectedUser.default_trade_qty);
      if (value === null) {
        setTradeQtyDraft("");
      } else {
        setTradeQtyDraft(String(value));
      }
      setTradeQtyError("");
    } else {
      setTradeQtyDraft("");
      setTradeQtyError("");
      setPremiumEdited(false);
      setCurrentEdited(false);
    }
  }, [selectedUser, parseNumeric]);

  useEffect(() => {
    if (!selectedUserId) {
      setOptionExpiries([]);
      setOptionStrikes([]);
      setOptionPrice(null);
      return;
    }
    fetchOptionExpiries(newOption.optionType);
  }, [selectedUserId, fetchOptionExpiries, newOption.optionType]);

  const evaluateTrend = useCallback((side, entry, current) => {
    if (!Number.isFinite(entry) || !Number.isFinite(current)) return 0;
    const normalizedSide = (side || "buy").toLowerCase();
    const diff = normalizedSide === "sell" ? entry - current : current - entry;
    if (diff > 1e-6) return 1;
    if (diff < -1e-6) return -1;
    return 0;
  }, []);

  const getPriceTrendInputClass = useCallback(
    (position, edits) => {
      const entry =
        parseNumeric(edits?.entry_price ?? position.entry_price) ?? 0;
      const current =
        parseNumeric(edits?.current_price ?? position.current_price) ?? entry;
      const side = (edits?.side ?? position.side ?? "buy").toLowerCase();
      const trend = evaluateTrend(side, entry, current);
      if (trend > 0)
        return "text-emerald-400 border-emerald-500 focus:border-emerald-400";
      if (trend < 0) return "text-red-400 border-red-500 focus:border-red-400";
      return "";
    },
    [parseNumeric, evaluateTrend]
  );

  const formatTimestamp = useCallback((iso) => {
    if (!iso) return "--";
    const normalizedIso = /[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`;
    const date = new Date(normalizedIso);
    if (Number.isNaN(date.getTime())) return iso;
    return `${date.toISOString().replace("T", " ").replace("Z", "")} UTC`;
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
      // Initialize SPX multipliers from user data (only if not already edited)
      setSpxMultipliers((prev) => {
        const updated = { ...prev };
        data.forEach((u) => {
          if (!(u.user_id in updated)) {
            updated[u.user_id] = u.spx_qty_multiplier || 1.0;
          }
        });
        return updated;
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Error fetching users:", err);
      setStatus({ type: "error", message: "Failed to load users." });
    }
  }, [handleLogout]);

  const handleExitAllPositionsGlobal = useCallback(async () => {
    if (
      !window.confirm(
        "Exit ALL open grid positions for ALL users? Manual entries will NOT be closed. This action cannot be undone."
      )
    ) {
      return;
    }
    setExitAllGlobalLoading(true);
    try {
      const res = await exitAllPositionsGlobal();
      setStatus({
        type: "success",
        message:
          res.message ||
          `Closed ${res.total_positions_closed || 0} positions for ${
            res.users_affected?.length || 0
          } users.`,
      });
      await fetchUsers();
    } catch (err) {
      console.error("Failed to exit all positions globally", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail ||
          err.message ||
          "Unable to exit all positions.",
      });
    } finally {
      setExitAllGlobalLoading(false);
    }
  }, [fetchUsers]);

  const fetchBalanceRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const data = await getBalanceRequestsAdmin("pending");
      setBalanceRequests(data);
    } catch (err) {
      console.error("Error fetching balance requests:", err);
      setStatus({ type: "error", message: "Failed to load funding requests." });
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  const fetchAdminWithdrawalRequests = useCallback(async () => {
    setLoadingWithdrawalRequests(true);
    try {
      const data = await getAdminWithdrawalRequests();
      setAdminWithdrawalRequests(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Error fetching admin withdrawal requests:", err);
    } finally {
      setLoadingWithdrawalRequests(false);
    }
  }, [handleLogout]);

  const handleInitiateAdminWithdrawal = useCallback(async () => {
    if (!selectedUser) {
      setStatus({ type: "error", message: "Select a user first." });
      return;
    }
    const amount = parseFloat(adminWithdrawalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setStatus({
        type: "error",
        message: "Enter a valid amount greater than zero.",
      });
      return;
    }
    if (amount > (selectedUser.balance || 0)) {
      setStatus({
        type: "error",
        message: `Amount exceeds user balance ($${selectedUser.balance?.toFixed(
          2
        )}).`,
      });
      return;
    }
    setInitiatingWithdrawal(true);
    try {
      const res = await initiateAdminWithdrawal(
        selectedUser.user_id,
        amount,
        adminWithdrawalNote.trim() || null
      );
      setStatus({
        type: "success",
        message:
          res.message ||
          `Withdrawal request sent to ${selectedUser.email} for approval.`,
      });
      setAdminWithdrawalAmount("");
      setAdminWithdrawalNote("");
      fetchAdminWithdrawalRequests();
    } catch (err) {
      console.error("Failed to initiate admin withdrawal:", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail ||
          "Failed to initiate withdrawal request.",
      });
    } finally {
      setInitiatingWithdrawal(false);
    }
  }, [
    selectedUser,
    adminWithdrawalAmount,
    adminWithdrawalNote,
    fetchAdminWithdrawalRequests,
  ]);

  const handleCancelAdminWithdrawal = useCallback(
    async (requestId) => {
      if (!window.confirm("Cancel this withdrawal request?")) return;
      setCancellingWithdrawalId(requestId);
      try {
        await cancelAdminWithdrawal(requestId);
        setStatus({
          type: "success",
          message: "Withdrawal request cancelled.",
        });
        fetchAdminWithdrawalRequests();
      } catch (err) {
        console.error("Failed to cancel withdrawal:", err);
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail ||
            "Failed to cancel withdrawal request.",
        });
      } finally {
        setCancellingWithdrawalId(null);
      }
    },
    [fetchAdminWithdrawalRequests]
  );

  useEffect(() => {
    fetchUsers();
    fetchBalanceRequests();
    fetchAdminWithdrawalRequests();
  }, [fetchUsers, fetchBalanceRequests, fetchAdminWithdrawalRequests]);

  const fetchAdminPnL = useCallback(async () => {
    if (!users || users.length === 0) return;
    setPnlLoading(true);
    try {
      const paramsBase = {};
      if (pnlRange === "custom") {
        if (!pnlCustom.start || !pnlCustom.end) {
          setPnlLoading(false);
          return;
        }
        paramsBase.start_date = pnlCustom.start;
        paramsBase.end_date = pnlCustom.end;
      } else if (pnlRange) {
        paramsBase.period = pnlRange;
      }
      const entries = await Promise.all(
        users.map(async (u) => {
          try {
            const data = await getUserHistoryAdmin(u.user_id, paramsBase);
            return [u.user_id, Number(data?.summary?.closed_pnl_total ?? 0)];
          } catch {
            return [u.user_id, 0];
          }
        })
      );
      const map = {};
      entries.forEach(([uid, pnl]) => {
        map[uid] = pnl;
      });
      setPnlByUser(map);
    } finally {
      setPnlLoading(false);
    }
  }, [users, pnlRange, pnlCustom.start, pnlCustom.end]);

  // Trigger PnL fetch when preset period changes (not custom)
  // This replaces the previous auto-trigger that was causing polling storm
  useEffect(() => {
    if (pnlRange && pnlRange !== "custom" && users.length > 0) {
      fetchAdminPnL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnlRange, users.length > 0]);

  const sendDailyPnlTelegram = useCallback(async () => {
    try {
      setPnlSendStatus({
        type: "info",
        message: "Sending report to Telegram...",
      });
      const token = getToken?.();
      const res = await fetch(
        `${API_BASE_URL}/admin/reports/daily-pnl/telegram`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed");
      setPnlSendStatus({ type: "success", message: "Telegram report sent." });
    } catch (e) {
      setPnlSendStatus({
        type: "error",
        message: "Unable to send Telegram report.",
      });
    } finally {
      setTimeout(() => setPnlSendStatus(null), 4000);
    }
  }, []);

  const sendDailyPnlEmail = useCallback(async () => {
    try {
      setPnlSendStatus({
        type: "info",
        message: "Emailing report to admins...",
      });
      const token = getToken?.();
      const res = await fetch(`${API_BASE_URL}/admin/reports/daily-pnl/email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      setPnlSendStatus({ type: "success", message: "Email report sent." });
    } catch (e) {
      setPnlSendStatus({ type: "error", message: "Unable to email report." });
    } finally {
      setTimeout(() => setPnlSendStatus(null), 4000);
    }
  }, []);

  const downloadDailyPnlCsv = useCallback(async () => {
    try {
      const token = getToken?.();
      const y = new Date(Date.now() - 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      const url = new URL(`${API_BASE_URL}/admin/reports/daily-pnl.csv`);
      url.searchParams.set("date", y);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `daily-pnl-${y}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setPnlSendStatus({ type: "error", message: "Unable to download CSV." });
      setTimeout(() => setPnlSendStatus(null), 4000);
    }
  }, []);

  const handleDownloadLedgerCsv = useCallback(async () => {
    setLedgerDownloading(true);
    try {
      let startDate = null;
      let endDate = null;

      if (ledgerDateRange === "7d") {
        const now = new Date();
        endDate = now.toISOString().slice(0, 10);
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10);
      } else if (ledgerDateRange === "30d") {
        const now = new Date();
        endDate = now.toISOString().slice(0, 10);
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10);
      } else if (ledgerDateRange === "custom") {
        startDate = ledgerCustomDates.start || null;
        endDate = ledgerCustomDates.end || null;
      }
      // "all" means no date filter

      const userId = ledgerUserId ? parseInt(ledgerUserId, 10) : null;
      await downloadLedgerCsvAdmin(userId, startDate, endDate);
      setStatus({
        type: "success",
        message: "Ledger CSV downloaded successfully.",
      });
    } catch (err) {
      console.error("Failed to download ledger CSV", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail ||
          err.message ||
          "Failed to download ledger CSV.",
      });
    } finally {
      setLedgerDownloading(false);
    }
  }, [ledgerUserId, ledgerDateRange, ledgerCustomDates]);

  useEffect(() => {
    if (isPollingPaused) return undefined; // Don't poll during operations
    const interval = setInterval(() => {
      fetchUsers();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUsers, isPollingPaused]);

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [status]);

  // Auto-scroll only when explicitly managing a user (or on mobile)
  useEffect(() => {
    if (!selectedUserId || !manageSectionRef.current) return;
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches;
    if (scrollOnManage || isMobile) {
      try {
        manageSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch (_) {}
      if (scrollOnManage) setScrollOnManage(false);
    }
  }, [selectedUserId, scrollOnManage]);

  const loadPositions = useCallback(
    async (userId, { silent = false } = {}) => {
      if (!silent) {
        setLoadingPositions(true);
      }
      try {
        const data = await getUserPositionsAdmin(userId);
        setPositionsByUser((prev) => ({ ...prev, [userId]: data }));
        setPositionEdits((prev) => ({
          ...prev,
          [userId]: data.reduce((acc, pos) => {
            acc[pos.position_id] = mapPositionToEdits(pos);
            return acc;
          }, {}),
        }));
        updateUserAggregates(userId, data);
      } catch (err) {
        if (err?.response?.status === 401) {
          handleLogout();
          return;
        }
        console.error("Error fetching positions:", err);
        setStatus({ type: "error", message: "Failed to load positions." });
      } finally {
        if (!silent) {
          setLoadingPositions(false);
        }
      }
    },
    [handleLogout, mapPositionToEdits, updateUserAggregates]
  );

  useEffect(() => {
    if (!positionsWsUrl) {
      return undefined;
    }

    let ws;
    let reconnectTimer = null;

    const cleanupSocket = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (ws) {
        try {
          ws.close();
        } catch (_) {}
        ws = null;
      }
    };

    const handleMessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const type = payload?.type;

        if (type === "snapshot" && Array.isArray(payload.payload)) {
          const grouped = payload.payload.reduce((acc, position) => {
            const uid = position.user_id;
            if (!acc[uid]) acc[uid] = [];
            acc[uid].push(position);
            return acc;
          }, {});

          setPositionsByUser((prev) => ({ ...prev, ...grouped }));
          setPositionEdits((prev) => {
            const next = { ...prev };
            Object.entries(grouped).forEach(([uid, positions]) => {
              next[uid] = positions.reduce((acc, position) => {
                acc[position.position_id] = mapPositionToEdits(position);
                return acc;
              }, {});
            });
            return next;
          });
          Object.entries(grouped).forEach(([uid, positions]) => {
            updateUserAggregates(Number(uid), positions);
          });
          return;
        }

        if (type !== "position_update" || !payload.payload) return;
        const position = payload.payload;
        const userId = position.user_id;
        let nextPositionsForUser;

        setPositionsByUser((prev) => {
          const existing = prev[userId] || [];
          let next;
          if ((position.status || "").toLowerCase() === "closed") {
            next = existing.filter(
              (pos) => pos.position_id !== position.position_id
            );
          } else {
            const idx = existing.findIndex(
              (pos) => pos.position_id === position.position_id
            );
            if (idx >= 0) {
              next = [...existing];
              next[idx] = position;
            } else {
              next = [...existing, position];
            }
          }
          nextPositionsForUser = next;
          return { ...prev, [userId]: next };
        });

        if (nextPositionsForUser) {
          if ((position.status || "").toLowerCase() === "closed") {
            setPositionEdits((prev) => {
              const existing = { ...(prev[userId] || {}) };
              delete existing[position.position_id];
              return { ...prev, [userId]: existing };
            });
            setClosingPositionId((prev) =>
              prev === position.position_id ? null : prev
            );
          } else {
            setPositionEdits((prev) => ({
              ...prev,
              [userId]: {
                ...(prev[userId] || {}),
                [position.position_id]: mapPositionToEdits(position),
              },
            }));
          }
          updateUserAggregates(userId, nextPositionsForUser);
        }
      } catch (err) {
        console.error("Failed to handle position message", err);
      }
    };

    const connect = () => {
      cleanupSocket();
      try {
        ws = new WebSocket(positionsWsUrl);
      } catch (err) {
        console.error("Positions websocket creation failed", err);
        reconnectTimer = setTimeout(connect, 5000);
        return;
      }
      positionsSocketRef.current = ws;

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ type: "ping" }));
        } catch (_) {}
        heartbeatRef.current = setInterval(() => {
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          try {
            ws.send(JSON.stringify({ type: "ping" }));
          } catch (_) {}
        }, 30000);
      };

      ws.onmessage = handleMessage;

      const scheduleReconnect = () => {
        cleanupSocket();
        if (!reconnectTimer) {
          reconnectTimer = setTimeout(connect, 5000);
        }
      };

      ws.onclose = scheduleReconnect;
      ws.onerror = scheduleReconnect;
    };

    connect();

    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      cleanupSocket();
      positionsSocketRef.current = null;
    };
  }, [mapPositionToEdits, updateUserAggregates, positionsWsUrl]);

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    setScrollOnManage(true);
    loadPositions(userId);
    fetchUserBots(userId); // Load multi-bot configurations
  };

  useEffect(() => {
    if (!selectedUserId) return undefined;
    if (isPollingPaused) return undefined; // Don't poll during operations
    const interval = setInterval(() => {
      loadPositions(selectedUserId, { silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedUserId, loadPositions, isPollingPaused]);

  useEffect(() => {
    if (!newOption.expiry) {
      setOptionStrikes([]);
      setOptionPrice(null);
      return;
    }
    fetchOptionStrikes(newOption.expiry, newOption.optionType);
  }, [newOption.expiry, newOption.optionType, fetchOptionStrikes]);

  useEffect(() => {
    if (!newOption.expiry || newOption.strike === "") {
      setOptionPrice(null);
      return;
    }
    const strikeValue = Number(newOption.strike);
    if (!Number.isFinite(strikeValue)) {
      setOptionPrice(null);
      return;
    }
    fetchOptionPrice(newOption.expiry, strikeValue, newOption.optionType);
  }, [
    newOption.expiry,
    newOption.strike,
    newOption.optionType,
    fetchOptionPrice,
  ]);

  const handleBotToggle = useCallback(
    async (userId, active, username) => {
      setBotUpdating(userId);
      try {
        await toggleUserBotAdmin(userId, active);
        await fetchUsers();
        setStatus({
          type: "success",
          message: `Bot ${active ? "started" : "stopped"}${
            username ? ` for ${username}` : ""
          }`,
        });
      } catch (err) {
        console.error("Failed to toggle bot", err);
        setStatus({
          type: "error",
          message: err.response?.data?.detail || "Unable to update bot status.",
        });
      } finally {
        setBotUpdating(null);
      }
    },
    [fetchUsers]
  );

  // --- Multi-Bot Management Handlers ---
  const fetchUserBots = useCallback(async (userId) => {
    try {
      const data = await getUserBotsAdmin(userId);
      setUserBots((prev) => ({
        ...prev,
        [userId]: data.bots.reduce((acc, bot) => {
          acc[bot.symbol] = bot;
          return acc;
        }, {}),
      }));
    } catch (err) {
      console.error("Failed to fetch user bots", err);
      setStatus({
        type: "error",
        message: "Failed to load bot configurations.",
      });
    }
  }, []);

  const handleMultiBotToggle = useCallback(
    async (userId, symbol, shouldStart) => {
      setMultiBotUpdating({ userId, symbol });
      try {
        if (shouldStart) {
          await startUserBotAdmin(userId, symbol);
          setStatus({ type: "success", message: `${symbol} bot started.` });
        } else {
          await stopUserBotAdmin(userId, symbol);
          setStatus({ type: "success", message: `${symbol} bot stopped.` });
        }
        await fetchUserBots(userId);
        await fetchUsers();
      } catch (err) {
        console.error("Failed to toggle bot", err);
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail ||
            `Failed to ${shouldStart ? "start" : "stop"} ${symbol} bot.`,
        });
      } finally {
        setMultiBotUpdating(null);
      }
    },
    [fetchUserBots, fetchUsers]
  );

  const handleBotConfigSave = useCallback(
    async (userId, symbol) => {
      const editKey = `${userId}-${symbol}`;
      const edits = botConfigEdits[editKey];
      if (!edits) return;

      // OPTIMISTIC UPDATE - Update UI immediately for instant feedback
      setUserBots((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [symbol]: {
            ...(prev[userId]?.[symbol] || {}),
            default_qty:
              edits.default_qty !== undefined
                ? parseFloat(edits.default_qty)
                : prev[userId]?.[symbol]?.default_qty,
            grid_spacing_percent:
              edits.grid_spacing_percent !== undefined
                ? parseFloat(edits.grid_spacing_percent)
                : prev[userId]?.[symbol]?.grid_spacing_percent,
            take_profit_percent:
              edits.take_profit_percent !== undefined
                ? parseFloat(edits.take_profit_percent)
                : prev[userId]?.[symbol]?.take_profit_percent,
            slippage_points:
              edits.slippage_points !== undefined
                ? parseFloat(edits.slippage_points)
                : prev[userId]?.[symbol]?.slippage_points,
            max_grid:
              edits.max_grid !== undefined
                ? parseInt(edits.max_grid, 10)
                : prev[userId]?.[symbol]?.max_grid,
          },
        },
      }));

      // Clear edit state immediately for instant UI feedback
      setBotConfigEdits((prev) => {
        const next = { ...prev };
        delete next[editKey];
        return next;
      });

      // Brief loading state and pause polling
      setMultiBotUpdating({ userId, symbol });
      setIsPollingPaused(true);

      try {
        const payload = {};
        if (edits.default_qty !== undefined) {
          payload.default_qty = parseFloat(edits.default_qty);
        }
        if (edits.grid_spacing_percent !== undefined) {
          payload.grid_spacing_percent = parseFloat(edits.grid_spacing_percent);
        }
        if (edits.take_profit_percent !== undefined) {
          payload.take_profit_percent = parseFloat(edits.take_profit_percent);
        }
        if (edits.slippage_points !== undefined) {
          payload.slippage_points = parseFloat(edits.slippage_points);
        }
        if (edits.max_grid !== undefined) {
          payload.max_grid = parseInt(edits.max_grid, 10);
        }

        await updateUserBotAdmin(userId, symbol, payload);
        setStatus({
          type: "success",
          message: `${symbol} settings saved!`,
        });

        // Fire and forget background refresh - don't await
        fetchUserBots(userId).catch(console.error);
      } catch (err) {
        console.error("Failed to update bot config", err);
        setStatus({
          type: "error",
          message:
            err.response?.data?.detail ||
            `Failed to update ${symbol} configuration.`,
        });
        // On error, refresh to get correct state
        fetchUserBots(userId).catch(console.error);
      } finally {
        setMultiBotUpdating(null);
        setIsPollingPaused(false);
      }
    },
    [botConfigEdits, fetchUserBots]
  );

  const handleStopAllBots = useCallback(
    async (userId) => {
      if (!window.confirm("Stop all bots for this user?")) return;
      setMultiBotUpdating({ userId, symbol: "ALL" });
      try {
        await stopAllUserBotsAdmin(userId);
        setStatus({ type: "success", message: "All bots stopped." });
        await fetchUserBots(userId);
        await fetchUsers();
      } catch (err) {
        console.error("Failed to stop all bots", err);
        setStatus({
          type: "error",
          message: err.response?.data?.detail || "Failed to stop all bots.",
        });
      } finally {
        setMultiBotUpdating(null);
      }
    },
    [fetchUserBots, fetchUsers]
  );

  const handleNewUserFieldChange = (field, value) => {
    setNewUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      setStatus({
        type: "error",
        message: "Username and password are required.",
      });
      return;
    }

    setCreatingUser(true);
    try {
      await adminCreateUser({
        username: newUser.username.trim(),
        password: newUser.password,
        email: newUser.email.trim() || undefined,
      });
      setStatus({ type: "success", message: "User created successfully." });
      setNewUser({ username: "", email: "", password: "" });
      fetchUsers();
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Failed to create user", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Unable to create user.",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  // -- Manual password set ---------------------------------------------
  // Only touches the selected user's login credential. Balances, positions,
  // bots and trade data are never read or written by this flow.
  const currentAdminUsername = useMemo(() => {
    try {
      return localStorage.getItem("username") || "";
    } catch (err) {
      return "";
    }
  }, []);

  const pwdResetTarget = useMemo(
    () =>
      users.find((u) => String(u.user_id) === String(pwdReset.userId)) || null,
    [users, pwdReset.userId]
  );

  // An admin may set their own password, but not another admin's.
  // The backend enforces this too; this only keeps the UI honest.
  const pwdResetBlocked = Boolean(
    pwdResetTarget &&
      pwdResetTarget.role === "admin" &&
      pwdResetTarget.username !== currentAdminUsername
  );

  const handlePwdResetFieldChange = (field, value) => {
    setPwdReset((prev) => ({ ...prev, [field]: value }));
    setPwdResetError("");
    setPwdResetDone(null);
  };

  const handleGeneratePassword = () => {
    const alphabet =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%&*";
    let generated = "";
    const cryptoObj = typeof window !== "undefined" ? window.crypto : null;
    if (cryptoObj && cryptoObj.getRandomValues) {
      const buf = new Uint32Array(14);
      cryptoObj.getRandomValues(buf);
      buf.forEach((n) => {
        generated += alphabet[n % alphabet.length];
      });
    } else {
      for (let i = 0; i < 14; i += 1) {
        generated += alphabet[Math.floor(Math.random() * alphabet.length)];
      }
    }
    setPwdReset((prev) => ({
      ...prev,
      password: generated,
      confirm: generated,
    }));
    setPwdResetShow(true);
    setPwdResetError("");
    setPwdResetDone(null);
  };

  const handleCopyNewPassword = async () => {
    if (!pwdResetDone?.password) return;
    try {
      await navigator.clipboard.writeText(pwdResetDone.password);
      setPwdResetCopied(true);
      setTimeout(() => setPwdResetCopied(false), 2000);
    } catch (err) {
      console.warn("Clipboard unavailable", err);
      setPwdResetError(
        "Could not copy - select the password and copy it manually."
      );
    }
  };

  const handleSetUserPassword = async () => {
    setPwdResetDone(null);

    const targetId = Number.parseInt(pwdReset.userId, 10);
    if (!Number.isFinite(targetId)) {
      setPwdResetError("Select a user first.");
      return;
    }

    // /auth/login trims the submitted password, so trim here as well to make
    // sure the password handed to the user is exactly the one that will work.
    const password = (pwdReset.password || "").trim();
    const confirm = (pwdReset.confirm || "").trim();

    if (password.length < 8) {
      setPwdResetError("Password must be at least 8 characters.");
      return;
    }
    if (password.length > 72) {
      setPwdResetError("Password must be at most 72 characters.");
      return;
    }
    if (password !== confirm) {
      setPwdResetError("The two passwords do not match.");
      return;
    }

    const label = pwdResetTarget?.username || `user ${targetId}`;
    const confirmed = window.confirm(
      `Set a new login password for ${label}?\n\n` +
        "Their current password will stop working immediately. " +
        "No email is sent, so pass the new password to them yourself."
    );
    if (!confirmed) return;

    setPwdResetError("");
    setPwdResetSaving(true);
    try {
      const data = await adminResetUserPassword(
        targetId,
        password,
        pwdReset.forceLogout
      );
      setPwdResetDone({
        username: data.username || label,
        password,
        forcedLogout: Boolean(data.forced_logout),
      });
      setPwdReset((prev) => ({ ...prev, password: "", confirm: "" }));
      setPwdResetShow(false);
      setStatus({
        type: "success",
        message: `Password updated for ${data.username || label}.`,
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Failed to set user password", err);
      setPwdResetError(
        err.response?.data?.detail || "Unable to set the password."
      );
    } finally {
      setPwdResetSaving(false);
    }
  };

  const handleSaveTradeQty = async () => {
    if (!selectedUser) {
      return;
    }
    const parsed = Number.parseFloat(tradeQtyDraft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setTradeQtyError("Enter a quantity greater than zero.");
      return;
    }

    setTradeQtyError("");
    setSavingTradeQty(true);
    try {
      const { default_trade_qty, bot_restarted } =
        await updateUserTradeSettingsAdmin(selectedUser.user_id, parsed);
      setUsers((prev) =>
        prev.map((user) =>
          user.user_id === selectedUser.user_id
            ? { ...user, default_trade_qty }
            : user
        )
      );
      setTradeQtyDraft(String(default_trade_qty));
      setTradeQtyError("");
      setStatus({
        type: "success",
        message:
          `Updated default quantity for ${selectedUser.username}.` +
          (bot_restarted ? " Bot restarted to apply changes." : ""),
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error("Failed to update trade quantity", err);
      setTradeQtyError(
        err.response?.data?.detail || "Unable to update default quantity."
      );
    } finally {
      setSavingTradeQty(false);
    }
  };

  const handleApplyManualSwap = async () => {
    if (!selectedUser) return;
    const amount = parseFloat(manualSwap.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setStatus({
        type: "error",
        message: "Enter a valid swap amount greater than zero.",
      });
      return;
    }
    if (
      !window.confirm(
        `Apply swap charge of $${amount.toFixed(2)} to ${
          selectedUser.username
        }?`
      )
    ) {
      return;
    }
    setApplyingSwap(true);
    try {
      const payload = { amount };
      if (manualSwap.startDate) payload.start_date = manualSwap.startDate;
      if (manualSwap.endDate) payload.end_date = manualSwap.endDate;
      if (manualSwap.notes.trim()) payload.notes = manualSwap.notes.trim();
      const res = await applyManualSwapAdmin(selectedUser.user_id, payload);
      setStatus({
        type: "success",
        message: `Swap charge of $${
          res.amount_deducted?.toFixed(2) || amount.toFixed(2)
        } applied. New balance: $${res.new_balance?.toFixed(2) || "—"}`,
      });
      setManualSwap({ amount: "", startDate: "", endDate: "", notes: "" });
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === selectedUser.user_id
            ? { ...u, balance: res.new_balance ?? u.balance }
            : u
        )
      );
    } catch (err) {
      console.error("Failed to apply manual swap", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Unable to apply swap charge.",
      });
    } finally {
      setApplyingSwap(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (
      !window.confirm(`Remove user ${username}? This action cannot be undone.`)
    ) {
      return;
    }
    try {
      await adminDeleteUser(userId);
      setStatus({ type: "success", message: `User ${username} deleted.` });
      if (selectedUserId === userId) {
        setSelectedUserId(null);
      }
      fetchUsers();
    } catch (err) {
      console.error("Failed to delete user", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Unable to delete user.",
      });
    }
  };

  const handleEditChange = (userId, positionId, field, value) => {
    setPositionEdits((prev) => {
      const userEdits = { ...(prev[userId] || {}) };
      userEdits[positionId] = {
        ...(userEdits[positionId] || {}),
        [field]: value,
      };
      return { ...prev, [userId]: userEdits };
    });
  };

  const handleRequestAction = useCallback(
    async (request, action) => {
      let adminComment = "";
      let adjustedAmount = null;
      if (action === "reject") {
        const response = window.prompt(
          "Add a note for the user (optional):",
          ""
        );
        if (response === null) {
          return;
        }
        adminComment = response;
      } else if (action === "approve") {
        const response = window.prompt(
          "Confirm amount to approve (you can adjust it here):",
          typeof request.amount === "number" ? request.amount : ""
        );
        if (response === null) {
          return;
        }
        if (response.trim() !== "") {
          const parsed = Number(response);
          if (!Number.isFinite(parsed) || parsed <= 0) {
            setStatus({
              type: "error",
              message: "Enter a valid amount greater than zero.",
            });
            return;
          }
          adjustedAmount = parsed;
        }
      }

      try {
        await decideBalanceRequestAdmin(
          request.request_id,
          action,
          adminComment,
          adjustedAmount
        );
        setStatus({
          type: "success",
          message:
            action === "approve" ? "Request approved." : "Request rejected.",
        });
        fetchBalanceRequests();
        fetchUsers();
      } catch (err) {
        console.error("Error updating request:", err);
        const detail = err.response?.data?.detail;
        setStatus({
          type: "error",
          message: detail || "Failed to process request.",
        });
      }
    },
    [fetchBalanceRequests, fetchUsers]
  );

  // Handler for direct balance addition
  const handleAddDirectBalance = async () => {
    if (!selectedUser) {
      setStatus({ type: "error", message: "Select a user first." });
      return;
    }
    const amount = parseFloat(directBalanceAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setStatus({
        type: "error",
        message: "Enter a valid amount greater than zero.",
      });
      return;
    }
    if (
      !window.confirm(
        `Add $${amount.toFixed(2)} to ${selectedUser.username}'s balance?`
      )
    ) {
      return;
    }
    setAddingDirectBalance(true);
    try {
      const token = getToken?.();
      const res = await fetch(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/add-balance`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount,
            note: directBalanceNote.trim() || null,
          }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to add balance");
      }
      const data = await res.json();
      setStatus({
        type: "success",
        message: `Added $${amount.toFixed(2)} to ${
          selectedUser.username
        }. New balance: $${data.new_balance?.toFixed(2) || "—"}`,
      });
      setDirectBalanceAmount("");
      setDirectBalanceNote("");
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === selectedUser.user_id
            ? { ...u, balance: data.new_balance ?? u.balance }
            : u
        )
      );
    } catch (err) {
      console.error("Failed to add direct balance", err);
      setStatus({
        type: "error",
        message: err.message || "Unable to add balance.",
      });
    } finally {
      setAddingDirectBalance(false);
    }
  };

  // --- Transaction Report helpers ---
  const getReportDateRange = useCallback((period, customDates) => {
    const now = new Date();
    const today = now.toISOString().split("T")[0];

    switch (period) {
      case "1day": {
        const yesterday = new Date(now - 24 * 60 * 60 * 1000);
        return {
          start: yesterday.toISOString().split("T")[0],
          end: today,
        };
      }
      case "7days": {
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        return {
          start: weekAgo.toISOString().split("T")[0],
          end: today,
        };
      }
      case "30days": {
        const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        return {
          start: monthAgo.toISOString().split("T")[0],
          end: today,
        };
      }
      case "thisMonth": {
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: firstOfMonth.toISOString().split("T")[0],
          end: today,
        };
      }
      case "lastMonth": {
        const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          start: firstOfLastMonth.toISOString().split("T")[0],
          end: lastOfLastMonth.toISOString().split("T")[0],
        };
      }
      case "custom":
        return customDates;
      default:
        return { start: today, end: today };
    }
  }, []);

  const handleDownloadTransactionReport = useCallback(async () => {
    if (!selectedUser) {
      setStatus({ type: "error", message: "Select a user first." });
      return;
    }

    const dateRange = getReportDateRange(reportPeriod, reportCustomDates);
    if (!dateRange.start || !dateRange.end) {
      setStatus({ type: "error", message: "Please select valid start and end dates." });
      return;
    }

    if (new Date(dateRange.start) > new Date(dateRange.end)) {
      setStatus({ type: "error", message: "Start date must be before end date." });
      return;
    }

    setReportDownloading(true);
    try {
      const result = await downloadUserTransactionReport(
        selectedUser.user_id,
        dateRange.start,
        dateRange.end
      );
      setStatus({
        type: "success",
        message: `Transaction report downloaded: ${result.filename}`,
      });
    } catch (err) {
      console.error("Failed to download transaction report", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || err.message || "Failed to download report.",
      });
    } finally {
      setReportDownloading(false);
    }
  }, [selectedUser, reportPeriod, reportCustomDates, getReportDateRange]);

  // --- Render helpers ---
  const renderSystemHealth = () => {
    const text = sysHealth?.system?.text;
    const basic = sysHealth?.status;
    return (
      <div className={`p-4 rounded mb-4 ${styles.card}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">System Health</h2>
            <span
              className={`text-sm ${
                basic?.status === "degraded" || basic?.status === "Stale"
                  ? "text-red-500"
                  : "text-green-600"
              }`}
            >
              ({basic?.status || "—"})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSystemHealthCollapsed((prev) => !prev)}
              className="px-3 py-1 rounded border border-current text-sm"
            >
              {systemHealthCollapsed ? "Expand" : "Collapse"}
            </button>
            <button
              onClick={fetchSystemHealth}
              className="px-3 py-1 rounded bg-blue-600 text-white text-sm disabled:opacity-60"
              disabled={sysHealthLoading}
            >
              {sysHealthLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        {!systemHealthCollapsed && (
          <>
            {text ? (
              <pre className={`${styles.mono} whitespace-pre-wrap`}>{text}</pre>
            ) : (
              <div className={styles.muted}>No health details yet.</div>
            )}
          </>
        )}
      </div>
    );
  };

  const handleSavePosition = async (userId, positionId) => {
    const edits = positionEdits[userId]?.[positionId];
    if (!edits) return;

    const isOption = Boolean(edits.is_option);

    // Convert display qty back to raw qty (multiply by lot multiplier for XAUUSD)
    const multiplier = getLotMultiplier(edits.symbol);
    const rawQty =
      edits.qty === "" || edits.qty === undefined
        ? undefined
        : Number(edits.qty) * multiplier;

    const payload = {
      symbol: edits.symbol,
      side: edits.side,
      qty: rawQty,
      entry_price:
        edits.entry_price === "" || edits.entry_price === undefined
          ? undefined
          : Number(edits.entry_price),
      current_price:
        edits.current_price === "" || edits.current_price === undefined
          ? undefined
          : Number(edits.current_price),
      option_type: edits.option_type,
      strike_price:
        edits.strike_price === "" || edits.strike_price === undefined
          ? undefined
          : Number(edits.strike_price),
      expiry_date: edits.expiry_date,
      option_premium:
        edits.option_premium === "" || edits.option_premium === undefined
          ? undefined
          : Number(edits.option_premium),
      is_option: isOption,
      status: edits.status,
      closed_price:
        edits.closed_price === "" || edits.closed_price === undefined
          ? undefined
          : Number(edits.closed_price),
    };

    if (!isOption) {
      payload.option_type = undefined;
      payload.strike_price = undefined;
      payload.expiry_date = undefined;
      payload.option_premium = undefined;
    }

    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (
        value === undefined ||
        value === "" ||
        (typeof value === "number" && Number.isNaN(value))
      ) {
        delete payload[key];
      }
    });

    try {
      setLoadingPositions(true);
      const updated = await updateUserPositionAdmin(positionId, payload);
      let nextPositionsForUser = null;
      setPositionsByUser((prev) => {
        const existing = prev[userId] || [];
        let nextList;
        if ((updated.status || "").toLowerCase() === "closed") {
          nextList = existing.filter((pos) => pos.position_id !== positionId);
        } else {
          const idx = existing.findIndex(
            (pos) => pos.position_id === positionId
          );
          if (idx >= 0) {
            nextList = [...existing];
            nextList[idx] = updated;
          } else {
            nextList = [...existing, updated];
          }
        }
        nextPositionsForUser = nextList;
        return { ...prev, [userId]: nextList };
      });
      if (nextPositionsForUser) {
        setPositionEdits((prev) => {
          const userEdits = { ...(prev[userId] || {}) };
          if ((updated.status || "").toLowerCase() === "closed") {
            delete userEdits[positionId];
          } else {
            userEdits[positionId] = mapPositionToEdits(updated);
          }
          return { ...prev, [userId]: userEdits };
        });
        updateUserAggregates(userId, nextPositionsForUser);
      }
      setStatus({ type: "success", message: "Position updated." });
    } catch (err) {
      console.error("Failed to update position:", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Update failed.",
      });
    } finally {
      setLoadingPositions(false);
    }
  };

  const handleExitPosition = async (userId, positionId) => {
    setClosingPositionId(positionId);
    try {
      const edits = positionEdits[userId]?.[positionId];
      const editedPrice =
        edits?.closed_price ?? edits?.current_price ?? edits?.option_premium;
      let payload = {};
      if (editedPrice !== undefined && editedPrice !== null) {
        const raw =
          typeof editedPrice === "string" ? editedPrice.trim() : editedPrice;
        if (raw !== "") {
          const parsedPrice = Number(raw);
          if (Number.isFinite(parsedPrice)) {
            payload = { price: parsedPrice };
          }
        }
      }

      await closeUserPositionAdmin(positionId, payload);
      await loadPositions(userId);
      setPositionEdits((prev) => {
        const next = { ...prev };
        if (next[userId]) {
          const edits = { ...next[userId] };
          delete edits[positionId];
          next[userId] = edits;
        }
        return next;
      });
      setStatus({ type: "success", message: "Position exited." });
      fetchUsers();
    } catch (err) {
      console.error("Failed to exit position:", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Unable to exit position.",
      });
    } finally {
      setClosingPositionId(null);
    }
  };

  const handleExitAllPositions = useCallback(async () => {
    if (!selectedUserId) return;
    setExitAllLoading(true);
    try {
      const options = { symbol: "BTCUSD" };
      const parsedSlippage = Number(exitAllSlippage);
      if (Number.isFinite(parsedSlippage) && parsedSlippage >= 0) {
        options.slippage = parsedSlippage;
      }
      const res = await exitAllPositionsAdmin(selectedUserId, options);
      await loadPositions(selectedUserId);
      await fetchUsers();
      const closedCount = res?.closed_count ?? 0;
      const skippedCount = Array.isArray(res?.skipped) ? res.skipped.length : 0;
      setStatus({
        type: "success",
        message: `Exited ${closedCount} crypto positions${
          skippedCount ? ` (skipped ${skippedCount})` : ""
        }`,
      });
    } catch (err) {
      console.error("Failed to exit positions:", err);
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Unable to exit positions.",
      });
    } finally {
      setExitAllLoading(false);
    }
  }, [selectedUserId, exitAllSlippage, loadPositions, fetchUsers]);

  const handleNewOptionChange = (field, value) => {
    if (field === "optionType") {
      const normalized = value.toLowerCase();
      setOptionStrikes([]);
      setOptionPrice(null);
      setPremiumEdited(false);
      setCurrentEdited(false);
      setNewOption((prev) => ({
        ...prev,
        optionType: normalized,
        strike: "",
        expiry: "",
        premium: "",
        current: "",
      }));
      fetchOptionExpiries(normalized);
      return;
    }

    if (field === "expiry") {
      setOptionPrice(null);
      setPremiumEdited(false);
      setCurrentEdited(false);
      setNewOption((prev) => ({
        ...prev,
        expiry: value,
        strike: "",
        premium: "",
        current: "",
      }));
      fetchOptionStrikes(value, newOption.optionType);
      return;
    }

    if (field === "strike") {
      setNewOption((prev) => ({ ...prev, strike: value }));
      setPremiumEdited(false);
      setCurrentEdited(false);
      if (!value) {
        setOptionPrice(null);
      }
      return;
    }

    if (field === "premium") {
      setPremiumEdited(true);
      setNewOption((prev) => ({ ...prev, premium: value }));
      return;
    }

    if (field === "current") {
      setCurrentEdited(true);
      setNewOption((prev) => ({ ...prev, current: value }));
      return;
    }

    setNewOption((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateOption = async () => {
    if (!selectedUser) {
      setStatus({ type: "error", message: "Select a user first." });
      return;
    }

    const { symbol, qty, premium, current, strike, expiry, optionType } =
      newOption;
    if (!symbol || !qty || !strike || !expiry) {
      setStatus({
        type: "error",
        message: "Symbol, quantity, strike, and expiry are required.",
      });
      return;
    }

    const quantity = Number(qty);
    const premiumValue = premium ? Number(premium) : optionPrice;
    const currentValue = current ? Number(current) : premiumValue;
    const strikeValue = Number(strike);

    if (
      [quantity, premiumValue, currentValue, strikeValue].some(
        (val) => !Number.isFinite(val)
      )
    ) {
      setStatus({ type: "error", message: "Enter valid numeric values." });
      return;
    }

    setCreatingOption(true);
    try {
      await createUserPositionAdmin(selectedUser.user_id, {
        symbol,
        side: "buy",
        qty: quantity,
        entry_price: premiumValue,
        current_price: currentValue,
        is_option: true,
        option_type: optionType,
        strike_price: strikeValue,
        expiry_date: expiry,
        option_premium: premiumValue,
      });
      setStatus({ type: "success", message: "Option position created." });
      setNewOption({
        symbol: "GLD",
        qty: "1",
        premium: "",
        current: "",
        strike: "",
        expiry: "",
        optionType,
      });
      setPremiumEdited(false);
      setCurrentEdited(false);
      await loadPositions(selectedUser.user_id);
      fetchUsers();
    } catch (err) {
      console.error("Failed to create option position", err);
      setStatus({
        type: "error",
        message:
          err.response?.data?.detail || "Unable to create option position.",
      });
    } finally {
      setCreatingOption(false);
    }
  };

  // ── SPX Options Panel Helpers ──
  const LEG_LABELS = ["CE BUY", "CE SELL", "PE BUY", "PE SELL"];

  const updateSpxLeg = (index, field, value) => {
    setSpxLegs((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
    setSpxPreview(null);
    setSpxResult(null);
  };

  const calculateLegPnl = (leg, multiplier = 1.0) => {
    if (!leg.enabled) return 0;
    const entry = parseFloat(leg.entry_premium) || 0;
    const exit = parseFloat(leg.exit_premium) || 0;
    const baseContracts = parseFloat(leg.qty_lots) || 0;
    const contracts = Math.floor(baseContracts * multiplier);
    if (leg.side === "buy") return (exit - entry) * contracts;
    return (entry - exit) * contracts;
  };

  // For single-user preview, use their multiplier
  const selectedUserMultiplier = selectedUser ? (spxMultipliers[selectedUser.user_id] || 1.0) : 1.0;
  const spxTotalPreviewPnl = spxLegs.reduce((s, l) => s + calculateLegPnl(l, spxApplyAll ? 1.0 : selectedUserMultiplier), 0);

  const handleSaveMultipliers = async () => {
    setSavingMultipliers(true);
    try {
      const mults = {};
      Object.entries(spxMultipliers).forEach(([uid, val]) => {
        mults[parseInt(uid)] = parseFloat(val) || 1.0;
      });
      await updateSpxMultipliersBulk(mults);
      setStatus({ type: "success", message: "SPX multipliers saved!" });
      fetchUsers();
    } catch (err) {
      setStatus({ type: "error", message: err.response?.data?.detail || "Failed to save multipliers" });
    } finally {
      setSavingMultipliers(false);
    }
  };
  const spxEnabledCount = spxLegs.filter((l) => l.enabled).length;

  const handleSpxPreview = () => {
    const enabledLegs = spxLegs.filter((l) => l.enabled);
    if (enabledLegs.length === 0) {
      setStatus({ type: "error", message: "Enable at least one leg" });
      return;
    }
    for (let i = 0; i < enabledLegs.length; i++) {
      const l = enabledLegs[i];
      if (!l.strike_price || parseFloat(l.strike_price) <= 0) {
        setStatus({ type: "error", message: `${l.option_type} ${l.side.toUpperCase()}: Strike price required` });
        return;
      }
      if (l.entry_premium === "" || parseFloat(l.entry_premium) < 0) {
        setStatus({ type: "error", message: `${l.option_type} ${l.side.toUpperCase()}: Valid entry premium required` });
        return;
      }
      if (l.exit_premium === "" || parseFloat(l.exit_premium) < 0) {
        setStatus({ type: "error", message: `${l.option_type} ${l.side.toUpperCase()}: Valid exit premium required` });
        return;
      }
    }
    if (!spxExpiryDate) {
      setStatus({ type: "error", message: "Expiry date is required" });
      return;
    }
    setSpxPreview({
      legs: enabledLegs.map((l, i) => ({
        label: `${l.option_type} ${l.side.toUpperCase()}`,
        strike: parseFloat(l.strike_price),
        entry: parseFloat(l.entry_premium),
        exit: parseFloat(l.exit_premium),
        contracts: parseFloat(l.qty_lots) || 100,
        lots: (parseFloat(l.qty_lots) || 100) / 100,
        pnl: calculateLegPnl(l),
      })),
      totalPnl: spxTotalPreviewPnl,
    });
    setStatus(null);
  };

  const handleSpxExecute = async () => {
    if (!spxPreview) {
      setStatus({ type: "error", message: "Preview trades before executing" });
      return;
    }
    if (!selectedUser) {
      setStatus({ type: "error", message: "Select a user first" });
      return;
    }
    const enabledLegs = spxLegs.filter((l) => l.enabled);
    // Determine target: selected user only, or all standard users
    const useAllUsers = spxApplyAll;
    const targetLabel = useAllUsers ? "ALL standard users" : selectedUser.username;
    const confirmMsg = `Execute ${enabledLegs.length} leg(s) for ${targetLabel}?\nEstimated PNL per user: $${spxTotalPreviewPnl.toFixed(2)}`;
    if (!window.confirm(confirmMsg)) return;

    setSpxLoading(true);
    setSpxResult(null);
    try {
      const payload = {
        legs: enabledLegs.map((l) => ({
          enabled: true,
          option_type: l.option_type,
          side: l.side,
          strike_price: parseFloat(l.strike_price),
          entry_premium: parseFloat(l.entry_premium),
          exit_premium: parseFloat(l.exit_premium),
          qty_lots: parseFloat(l.qty_lots) || 100,  // contracts
        })),
        trade_date: spxTradeDate,
        expiry_date: spxExpiryDate,
        notes: spxNotes,
        user_ids: useAllUsers ? null : [selectedUser.user_id],
      };
      const data = await executeSpxOptionsTrade(payload);
      setSpxResult(data);
      setSpxPreview(null);
      setStatus({ type: "success", message: `SPX trades executed! ${data.trades_created} positions created for ${data.users_affected} users. Total PNL: $${data.total_pnl}` });
      fetchUsers(); // refresh balances
    } catch (err) {
      console.error("SPX options trade failed", err);
      setStatus({ type: "error", message: err.response?.data?.detail || "SPX options trade failed" });
    } finally {
      setSpxLoading(false);
    }
  };

  const resetSpxPanel = () => {
    setSpxLegs([
      { enabled: true,  option_type: "CE", side: "buy",  strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
      { enabled: true,  option_type: "CE", side: "sell", strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
      { enabled: false, option_type: "PE", side: "buy",  strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
      { enabled: false, option_type: "PE", side: "sell", strike_price: "", entry_premium: "", exit_premium: "", qty_lots: 100 },
    ]);
    setSpxTradeDate(new Date().toISOString().slice(0, 10));
    setSpxExpiryDate("");
    setSpxNotes("");
    setSpxPreview(null);
    setSpxResult(null);
  };

  return (
    <div
      className={`p-6 min-h-screen transition-colors duration-200 ${styles.base}`}
    >
      {renderSystemHealth()}

      <div className="mb-6">
        <ManualGoldEntry
          users={users}
          styles={styles}
          onAfterExecute={fetchUsers}
          setStatus={setStatus}
        />
      </div>

      {/* Global Bot Control */}
      <div
        className={`mb-6 p-4 rounded-lg shadow transition-colors duration-200 ${styles.card}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Global Bot Control</h2>
            <p className={`text-xs ${styles.muted}`}>
              {globalBotStatus?.entries_paused
                ? "New entries are PAUSED for all bots. Existing positions continue to TP."
                : "Bots are running normally and can take new entries."}
            </p>
            {globalBotStatus && (
              <p className={`text-xs mt-1 ${styles.muted}`}>
                Total bots: {globalBotStatus.total_bots} | Running:{" "}
                {globalBotStatus.running_bots}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                globalBotLoading
                  ? "bg-gray-500 cursor-not-allowed"
                  : globalBotStatus?.entries_paused
                  ? "bg-emerald-600 hover:bg-emerald-500"
                  : "bg-red-600 hover:bg-red-500"
              }`}
              onClick={handleToggleGlobalEntries}
              disabled={globalBotLoading || !globalBotStatus}
            >
              {globalBotLoading
                ? "Updating..."
                : globalBotStatus?.entries_paused
                ? "Resume All Entries"
                : "Pause All Entries"}
            </button>
            <button
              className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                exitAllGlobalLoading
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-500"
              }`}
              onClick={handleExitAllPositionsGlobal}
              disabled={exitAllGlobalLoading}
              title="Exit all grid positions for all users (excludes manual entries)"
            >
              {exitAllGlobalLoading ? "Exiting..." : "Exit All Positions"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex gap-2">
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <button
            className="self-start md:self-auto bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      {/* User Realized PnL moved directly under header */}
      <div
        className={`mb-8 p-6 rounded-lg shadow-lg transition-colors duration-200 ${styles.card}`}
      >
        {/** Mobile collapse state */}
        {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
        {/* We intentionally initialize based on current viewport; users can toggle thereafter. */}
        {typeof window !== "undefined" &&
          !("pnlCollapsed" in window) &&
          (window.pnlCollapsed =
            !window.matchMedia("(min-width: 768px)").matches)}
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <p className="text-sm font-semibold">User Realized PnL</p>
            <p className={`text-xs ${styles.muted}`}>
              Daily, Weekly, Monthly, or Custom
            </p>
          </div>
          <div className="flex items-end gap-2">
            {/* Collapse toggle visible on small screens */}
            <button
              className="md:hidden text-sm px-3 py-1 rounded border border-current"
              onClick={() => {
                window.pnlCollapsed = !window.pnlCollapsed;
                // Trigger a rerender by updating state via a no-op setter pattern
                setPnlRange((v) => v);
              }}
            >
              {typeof window !== "undefined" && window.pnlCollapsed
                ? "Expand"
                : "Collapse"}
            </button>
            <select
              className={styles.input}
              value={pnlRange}
              onChange={(e) => setPnlRange(e.target.value)}
            >
              <option value="1d">1 Day</option>
              <option value="7d">7 Days</option>
              <option value="15d">15 Days</option>
              <option value="30d">30 Days</option>
              <option value="custom">Custom</option>
            </select>
            {pnlRange === "custom" && (
              <>
                <input
                  type="date"
                  className={styles.input}
                  value={pnlCustom.start}
                  onChange={(e) =>
                    setPnlCustom((s) => ({ ...s, start: e.target.value }))
                  }
                />
                <input
                  type="date"
                  className={styles.input}
                  value={pnlCustom.end}
                  onChange={(e) =>
                    setPnlCustom((s) => ({ ...s, end: e.target.value }))
                  }
                />
                <button
                  className="px-3 py-2 rounded-md text-sm bg-emerald-500 text-white"
                  onClick={fetchAdminPnL}
                  disabled={pnlLoading || !pnlCustom.start || !pnlCustom.end}
                >
                  {pnlLoading ? "Applying..." : "Apply"}
                </button>
              </>
            )}
          </div>
        </div>
        {pnlSendStatus && (
          <div
            className={`mb-2 text-sm ${
              pnlSendStatus.type === "error"
                ? "text-red-500"
                : pnlSendStatus.type === "success"
                ? "text-emerald-500"
                : styles.muted
            }`}
          >
            {pnlSendStatus.message}
          </div>
        )}
        <div className="flex gap-2 mb-3 flex-wrap">
          <button
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
            onClick={sendDailyPnlTelegram}
          >
            Send Telegram
          </button>
          <button
            className="px-3 py-1 rounded bg-emerald-600 text-white text-sm"
            onClick={sendDailyPnlEmail}
          >
            Send Email
          </button>
          <button
            className="px-3 py-1 rounded bg-gray-600 text-white text-sm"
            onClick={downloadDailyPnlCsv}
          >
            Download CSV
          </button>
        </div>
        <div
          className={`overflow-x-auto ${
            typeof window !== "undefined" && window.pnlCollapsed
              ? "hidden md:block"
              : ""
          }`}
        >
          <table className="min-w-full text-sm">
            <thead className="opacity-70">
              <tr>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Email</th>
                <th className="text-right p-2">Balance</th>
                <th className="text-right p-2">Open Positions</th>
                <th className="text-right p-2">Unrealized PnL</th>
                <th className="text-right p-2">Closed PnL (range)</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const realized = Number(pnlByUser[u.user_id] ?? 0);
                const realizedClass =
                  realized >= 0 ? "text-emerald-500" : "text-red-500";
                return (
                  <tr key={u.user_id} className="border-t border-dashed">
                    <td className="p-2">{u.username}</td>
                    <td className="p-2">{u.email}</td>
                    <td className="p-2 text-right">
                      {Number(u.balance ?? 0).toFixed(2)}
                    </td>
                    <td className="p-2 text-right">{u.open_positions}</td>
                    <td className="p-2 text-right">
                      {Number(u.total_unrealized_pnl ?? 0).toFixed(2)}
                    </td>
                    <td
                      className={`p-2 text-right font-medium ${realizedClass}`}
                    >
                      {realized.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td className="p-2 text-center opacity-70" colSpan={6}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
            {users.length > 0 && (
              <tfoot className="font-semibold border-t-2 border-current">
                <tr>
                  <td className="p-2" colSpan={2}>
                    Grand Total
                  </td>
                  <td className="p-2 text-right">
                    {users
                      .reduce((sum, u) => sum + Number(u.balance ?? 0), 0)
                      .toFixed(2)}
                  </td>
                  <td className="p-2 text-right">
                    {users.reduce(
                      (sum, u) => sum + Number(u.open_positions ?? 0),
                      0
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {users
                      .reduce(
                        (sum, u) => sum + Number(u.total_unrealized_pnl ?? 0),
                        0
                      )
                      .toFixed(2)}
                  </td>
                  <td
                    className={`p-2 text-right font-bold ${
                      users.reduce(
                        (sum, u) => sum + Number(pnlByUser[u.user_id] ?? 0),
                        0
                      ) >= 0
                        ? "text-emerald-500"
                        : "text-red-500"
                    }`}
                  >
                    {users
                      .reduce(
                        (sum, u) => sum + Number(pnlByUser[u.user_id] ?? 0),
                        0
                      )
                      .toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Admin Withdrawal Requests Section */}
      <div
        className={`mb-8 p-4 rounded-lg shadow transition-colors duration-200 ${styles.secondaryCard}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">Admin Withdrawal Requests</h2>
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={fetchAdminWithdrawalRequests}
            disabled={loadingWithdrawalRequests}
          >
            {loadingWithdrawalRequests ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        {loadingWithdrawalRequests ? (
          <p className={`text-sm ${styles.muted}`}>Loading requests…</p>
        ) : adminWithdrawalRequests.length === 0 ? (
          <p className={`text-sm ${styles.muted}`}>
            No admin withdrawal requests. Use the "Admin-Initiated Withdrawal"
            section when managing a user to create one.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="opacity-70">
                <tr>
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Note</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Expires</th>
                  <th className="text-center p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminWithdrawalRequests.map((req) => {
                  const statusColor =
                    req.status === "approved"
                      ? "text-emerald-500"
                      : req.status === "rejected"
                      ? "text-red-500"
                      : req.status === "cancelled"
                      ? "text-gray-400"
                      : req.status === "expired"
                      ? "text-yellow-500"
                      : "text-blue-400";
                  return (
                    <tr key={req.request_id} className="border-t border-dashed">
                      <td className="p-2">{req.request_id}</td>
                      <td className="p-2">
                        {req.username || `User ${req.user_id}`}
                      </td>
                      <td className="p-2">{req.email || "—"}</td>
                      <td className="p-2 text-right">
                        ${Number(req.amount || 0).toFixed(2)}
                      </td>
                      <td className={`p-2 font-medium ${statusColor}`}>
                        {req.status}
                      </td>
                      <td
                        className="p-2 max-w-xs truncate"
                        title={req.admin_note || ""}
                      >
                        {req.admin_note || "—"}
                      </td>
                      <td className="p-2 text-xs">
                        {req.created_at ? formatTimestamp(req.created_at) : "—"}
                      </td>
                      <td className="p-2 text-xs">
                        {req.expires_at ? formatTimestamp(req.expires_at) : "—"}
                      </td>
                      <td className="p-2 text-center">
                        {req.status === "pending" && (
                          <button
                            className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs"
                            onClick={() =>
                              handleCancelAdminWithdrawal(req.request_id)
                            }
                            disabled={cancellingWithdrawalId === req.request_id}
                          >
                            {cancellingWithdrawalId === req.request_id
                              ? "Cancelling..."
                              : "Cancel"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ledger Reports Section */}
      <div
        className={`mb-8 p-6 rounded-lg shadow-lg transition-colors duration-200 ${styles.card}`}
      >
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <p className="text-sm font-semibold">
              Ledger Reports (Pay-In / Pay-Out)
            </p>
            <p className={`text-xs ${styles.muted}`}>
              Export deposit and withdrawal transactions
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className={`text-xs ${styles.muted} block mb-1`}>User</label>
            <select
              className={styles.input}
              value={ledgerUserId}
              onChange={(e) => setLedgerUserId(e.target.value)}
            >
              <option value="">All Users</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.username} ({u.email || "no email"})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={`text-xs ${styles.muted} block mb-1`}>
              Date Range
            </label>
            <select
              className={styles.input}
              value={ledgerDateRange}
              onChange={(e) => setLedgerDateRange(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {ledgerDateRange === "custom" && (
            <>
              <div>
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  Start Date
                </label>
                <input
                  type="date"
                  className={styles.input}
                  value={ledgerCustomDates.start}
                  onChange={(e) =>
                    setLedgerCustomDates((prev) => ({
                      ...prev,
                      start: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className={`text-xs ${styles.muted} block mb-1`}>
                  End Date
                </label>
                <input
                  type="date"
                  className={styles.input}
                  value={ledgerCustomDates.end}
                  onChange={(e) =>
                    setLedgerCustomDates((prev) => ({
                      ...prev,
                      end: e.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}
          <button
            className={`px-4 py-2 rounded text-white text-sm ${
              ledgerDownloading
                ? "bg-emerald-400"
                : "bg-emerald-600 hover:bg-emerald-500"
            }`}
            onClick={handleDownloadLedgerCsv}
            disabled={ledgerDownloading}
          >
            {ledgerDownloading ? "Downloading..." : "Download Ledger CSV"}
          </button>
        </div>
        <p className={`text-xs ${styles.muted}`}>
          CSV includes: Date, User ID, Username, Email, Type (Pay-In/Pay-Out),
          Amount, Balance Before, Balance After, Note
        </p>
      </div>

      {status && (
        <div
          className={`mb-4 p-3 rounded text-sm ${
            status.type === "error" ? styles.statusError : styles.statusSuccess
          }`}
        >
          {status.message}
        </div>
      )}

      <div
        className={`mb-8 p-4 rounded-lg shadow transition-colors duration-200 ${styles.secondaryCard}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">Pending Funding Requests</h2>
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={fetchBalanceRequests}
            disabled={requestsLoading}
          >
            {requestsLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        {requestsLoading ? (
          <p className={`text-sm ${styles.muted}`}>Loading requests…</p>
        ) : balanceRequests.length === 0 ? (
          <p className={`text-sm ${styles.muted}`}>
            No pending requests. New funding requests will appear here.
          </p>
        ) : (
          <div className="space-y-3">
            {balanceRequests.map((req) => (
              <div
                key={req.request_id}
                className={`p-4 rounded-lg border ${styles.borderMuted}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-base">
                      {req.username || `User ${req.user_id}`}
                    </p>
                    <p className={`text-xs ${styles.muted}`}>
                      Account ID:{" "}
                      <span className="font-mono">
                        {req.account_id || "--"}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">
                      {req.request_type === "deposit"
                        ? "Deposit"
                        : "Withdrawal"}{" "}
                      · ${" "}
                      {Number(req.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className={`text-xs ${styles.muted}`}>
                      Submitted:{" "}
                      {req.created_at ? formatTimestamp(req.created_at) : "--"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    className="px-3 py-1 rounded bg-emerald-600 text-white text-sm"
                    onClick={() => handleRequestAction(req, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    className="px-3 py-1 rounded bg-red-600 text-white text-sm"
                    onClick={() => handleRequestAction(req, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className={`mb-8 p-4 rounded-lg shadow transition-colors duration-200 ${styles.secondaryCard}`}
      >
        <h2 className="text-xl font-semibold mb-3">Add New User</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <input
            className={styles.input}
            placeholder="Username"
            value={newUser.username}
            onChange={(e) =>
              handleNewUserFieldChange("username", e.target.value)
            }
          />
          <input
            type="email"
            className={styles.input}
            placeholder="Email (optional)"
            value={newUser.email}
            onChange={(e) => handleNewUserFieldChange("email", e.target.value)}
          />
          <input
            type="password"
            className={styles.input}
            placeholder="Temporary password"
            value={newUser.password}
            onChange={(e) =>
              handleNewUserFieldChange("password", e.target.value)
            }
          />
        </div>
        <button
          className="px-4 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-60"
          onClick={handleCreateUser}
          disabled={creatingUser}
        >
          {creatingUser ? "Creating..." : "Create User"}
        </button>
      </div>

      {/* Set User Password - manual fallback while reset emails are down */}
      <div
        className={`mb-8 p-4 rounded-lg shadow transition-colors duration-200 ${styles.secondaryCard}`}
      >
        <h2 className="text-xl font-semibold mb-1">Set User Password</h2>
        <p className={`text-xs mb-4 ${styles.muted}`}>
          Use this while password-reset emails are unavailable. It sets the
          login password for one user and changes nothing else on the account -
          balance, positions and bots are untouched. No email is sent, so share
          the new password with the user yourself.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <select
            className={styles.input}
            value={pwdReset.userId}
            onChange={(e) => handlePwdResetFieldChange("userId", e.target.value)}
          >
            <option value="">Select user...</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.username}
                {user.account_id ? ` (${user.account_id})` : ""}
                {user.role === "admin" ? " - admin" : ""}
              </option>
            ))}
          </select>
          <input
            type={pwdResetShow ? "text" : "password"}
            autoComplete="new-password"
            className={styles.input}
            placeholder="New password (min 8 characters)"
            value={pwdReset.password}
            onChange={(e) =>
              handlePwdResetFieldChange("password", e.target.value)
            }
          />
          <input
            type={pwdResetShow ? "text" : "password"}
            autoComplete="new-password"
            className={styles.input}
            placeholder="Confirm new password"
            value={pwdReset.confirm}
            onChange={(e) =>
              handlePwdResetFieldChange("confirm", e.target.value)
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={pwdResetShow}
              onChange={(e) => setPwdResetShow(e.target.checked)}
            />
            Show password
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={pwdReset.forceLogout}
              onChange={(e) =>
                handlePwdResetFieldChange("forceLogout", e.target.checked)
              }
            />
            Sign the user out of any active session
          </label>
          <button
            type="button"
            className="text-sm text-blue-500 hover:underline"
            onClick={handleGeneratePassword}
          >
            Generate strong password
          </button>
        </div>

        {pwdResetBlocked && (
          <p className="text-sm text-amber-500 mb-2">
            {pwdResetTarget?.username} is an admin account. Another admin's
            password cannot be set from here.
          </p>
        )}

        {pwdResetError && (
          <p className="text-sm text-red-500 mb-2">{pwdResetError}</p>
        )}

        <button
          className="px-4 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-60"
          onClick={handleSetUserPassword}
          disabled={pwdResetSaving || pwdResetBlocked || !pwdReset.userId}
        >
          {pwdResetSaving ? "Saving..." : "Set Password"}
        </button>

        {pwdResetDone && (
          <div className={`mt-4 p-3 rounded text-sm ${styles.statusSuccess}`}>
            <p className="mb-2">
              Password updated for{" "}
              <span className="font-semibold">{pwdResetDone.username}</span>.
              They can log in with it right away.
              {pwdResetDone.forcedLogout
                ? " Their active session was signed out."
                : ""}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <code className="font-mono px-2 py-1 rounded bg-black/20 break-all">
                {pwdResetDone.password}
              </code>
              <button
                type="button"
                className="px-2 py-1 rounded border border-current text-xs"
                onClick={handleCopyNewPassword}
              >
                {pwdResetCopied ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                className="px-2 py-1 rounded border border-current text-xs"
                onClick={() => setPwdResetDone(null)}
              >
                Hide
              </button>
            </div>
            <p className="text-xs mt-2 opacity-80">
              Shown once here - copy it before leaving this page.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {users.map((user) => (
          <div
            key={user.user_id}
            className={`p-4 rounded-lg shadow-lg transition duration-300 ${
              styles.card
            } ${selectedUserId === user.user_id ? "border-blue-500" : ""}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold">{user.username}</h2>
              <button
                className="text-sm text-blue-500 hover:underline"
                onClick={() => handleSelectUser(user.user_id)}
              >
                Manage
              </button>
            </div>
            <p className={`text-xs mb-2 ${styles.muted}`}>
              Account ID:{" "}
              <span className="font-mono">{user.account_id || "--"}</span>
            </p>
            <p className={`text-xs mb-2 ${styles.muted}`}>
              Email: {user.email || "--"}
            </p>
            <p className={`text-xs mb-3 ${styles.muted}`}>
              Bot Status: {user.bot_active ? "Running" : "Stopped"}
            </p>
            <p className="mb-1">
              Balance: <span className="font-semibold">${user.balance}</span>
            </p>
            <p className="mb-1">
              Default Qty:{" "}
              <span className="font-semibold">
                {formatQuantity(user.default_trade_qty)}
              </span>
            </p>
            <p className="mb-1">
              Open Positions:{" "}
              <span className="font-semibold">{user.open_positions}</span>
            </p>
            <p
              className={
                user.total_unrealized_pnl >= 0
                  ? "text-green-400"
                  : "text-red-400"
              }
            >
              Total P&L:{" "}
              <span className="font-semibold">{user.total_unrealized_pnl}</span>
            </p>
          </div>
        ))}
      </div>

      {/* Removed duplicate PnL card instance here */}
      {selectedUser && (
        <>
          <div
            ref={manageSectionRef}
            className={`p-6 rounded-lg shadow-lg transition-colors duration-200 ${styles.card}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">
                  Managing {selectedUser.username}
                </h2>
                <p className={`text-xs ${styles.muted}`}>
                  Account ID:{" "}
                  <span className="font-mono">
                    {selectedUser.account_id || "--"}
                  </span>
                </p>
                <p className={`text-xs ${styles.muted}`}>
                  Email: {selectedUser.email || "--"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="text-sm bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-white"
                  onClick={() => loadPositions(selectedUser.user_id)}
                  disabled={loadingPositions}
                >
                  {loadingPositions ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  className="text-sm px-4 py-2 rounded text-white"
                  style={{
                    backgroundColor: selectedUser.bot_active
                      ? "#dc2626"
                      : "#16a34a",
                    opacity: botUpdating === selectedUser.user_id ? 0.7 : 1,
                  }}
                  onClick={() =>
                    handleBotToggle(
                      selectedUser.user_id,
                      !selectedUser.bot_active,
                      selectedUser.username
                    )
                  }
                  disabled={botUpdating === selectedUser.user_id}
                >
                  {botUpdating === selectedUser.user_id
                    ? "Updating..."
                    : selectedUser.bot_active
                    ? "Stop Bot"
                    : "Start Bot"}
                </button>
              </div>
            </div>

            {/* Multi-Bot Control Panel */}
            <div className="mb-4">
              <BotControlPanel
                userId={selectedUser.user_id}
                userBots={userBots[selectedUser.user_id] || {}}
                botConfigEdits={botConfigEdits}
                setBotConfigEdits={setBotConfigEdits}
                botUpdating={multiBotUpdating}
                onToggle={handleMultiBotToggle}
                onConfigSave={handleBotConfigSave}
                onStopAll={handleStopAllBots}
                onManualPosition={async (symbol, side, tp, qty, notes, entryPrice) => {
                  const tpNum = parseFloat(tp);
                  const qtyNum = parseFloat(qty);
                  // Feature 4: Parse entry price (optional)
                  const entryPriceNum = entryPrice ? parseFloat(entryPrice) : null;
                  if (!Number.isFinite(tpNum) || tpNum <= 0) {
                    setStatus({
                      type: "error",
                      message: "Enter a valid take profit price.",
                    });
                    return;
                  }
                  if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
                    setStatus({
                      type: "error",
                      message: "Enter a valid quantity.",
                    });
                    return;
                  }
                  // Feature 4: Validate entry price if provided
                  if (entryPriceNum !== null && (!Number.isFinite(entryPriceNum) || entryPriceNum <= 0)) {
                    setStatus({
                      type: "error",
                      message: "Enter a valid entry price or leave empty for market price.",
                    });
                    return;
                  }
                  setManualPositionLoading(symbol);
                  try {
                    const res = await openManualPositionAdmin(
                      selectedUser.user_id,
                      symbol,
                      side,
                      tpNum,
                      qtyNum,
                      notes || null,
                      entryPriceNum  // Feature 4: Pass entry price to API
                    );
                    setStatus({
                      type: "success",
                      message:
                        res.message ||
                        `Manual ${side} position opened for ${symbol}.`,
                    });
                  } catch (err) {
                    setStatus({
                      type: "error",
                      message:
                        err.response?.data?.detail ||
                        "Failed to open manual position.",
                    });
                  } finally {
                    setManualPositionLoading(null);
                  }
                }}
                manualPositionLoading={manualPositionLoading}
                styles={styles}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>Balance</p>
                <p className="text-lg font-semibold">
                  ${selectedUser.balance?.toLocaleString()}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>Open Positions</p>
                <p className="text-lg font-semibold">
                  {selectedUser.open_positions}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>Bot Status</p>
                <p className="text-lg font-semibold">
                  {selectedUser.bot_active ? "Running" : "Stopped"}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>Default Qty</p>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    className={`${styles.input} flex-1`}
                    value={tradeQtyDraft}
                    onChange={(e) => {
                      setTradeQtyDraft(e.target.value);
                      if (tradeQtyError) {
                        setTradeQtyError("");
                      }
                    }}
                    placeholder="0.01"
                    disabled={savingTradeQty}
                  />
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${
                      savingTradeQty
                        ? "bg-blue-400"
                        : "bg-blue-600 hover:bg-blue-500"
                    }`}
                    onClick={handleSaveTradeQty}
                    disabled={savingTradeQty}
                  >
                    {savingTradeQty ? "Saving..." : "Save"}
                  </button>
                </div>
                {tradeQtyError ? (
                  <p className="text-xs text-red-400 mt-2">{tradeQtyError}</p>
                ) : (
                  <p className={`text-[11px] mt-2 ${styles.muted}`}>
                    Currently: {formatQuantity(selectedUser.default_trade_qty)}
                  </p>
                )}
              </div>
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>Manual Swap Charges</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Amount ($)
                    </label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="200.00"
                      value={manualSwap.amount}
                      onChange={(e) =>
                        setManualSwap((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      disabled={applyingSwap}
                    />
                  </div>
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Notes (optional)
                    </label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="Swap charges for Jan 2025"
                      value={manualSwap.notes}
                      onChange={(e) =>
                        setManualSwap((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      disabled={applyingSwap}
                    />
                  </div>
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      From Date (optional)
                    </label>
                    <input
                      className={styles.input}
                      type="date"
                      value={manualSwap.startDate}
                      onChange={(e) =>
                        setManualSwap((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      disabled={applyingSwap}
                    />
                  </div>
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      To Date (optional)
                    </label>
                    <input
                      className={styles.input}
                      type="date"
                      value={manualSwap.endDate}
                      onChange={(e) =>
                        setManualSwap((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      disabled={applyingSwap}
                    />
                  </div>
                </div>
                <p className={`text-[11px] mt-2 ${styles.muted}`}>
                  This will deduct the amount from the user's balance and log it
                  as a swap charge.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${
                      applyingSwap
                        ? "bg-orange-400"
                        : "bg-orange-600 hover:bg-orange-500"
                    }`}
                    onClick={handleApplyManualSwap}
                    disabled={applyingSwap || !manualSwap.amount}
                  >
                    {applyingSwap ? "Applying..." : "Apply Swap Charge"}
                  </button>
                </div>
              </div>
              {/* Direct Balance Addition Section */}
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>
                  Direct Balance Addition
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Amount ($)
                    </label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="1000.00"
                      value={directBalanceAmount}
                      onChange={(e) => setDirectBalanceAmount(e.target.value)}
                      disabled={addingDirectBalance}
                    />
                  </div>
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Note (optional)
                    </label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="Reason for addition"
                      value={directBalanceNote}
                      onChange={(e) => setDirectBalanceNote(e.target.value)}
                      disabled={addingDirectBalance}
                    />
                  </div>
                </div>
                <p className={`text-[11px] mt-2 ${styles.muted}`}>
                  Directly adds funds to user's balance without requiring
                  approval. Use for adjustments, bonuses, or corrections.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${
                      addingDirectBalance
                        ? "bg-emerald-400"
                        : "bg-emerald-600 hover:bg-emerald-500"
                    }`}
                    onClick={handleAddDirectBalance}
                    disabled={addingDirectBalance || !directBalanceAmount}
                  >
                    {addingDirectBalance ? "Adding..." : "Add Balance"}
                  </button>
                </div>
              </div>
              {/* Admin-Initiated Withdrawal Section */}
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>
                  Admin-Initiated Withdrawal
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Amount ($)
                    </label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="500.00"
                      value={adminWithdrawalAmount}
                      onChange={(e) => setAdminWithdrawalAmount(e.target.value)}
                      disabled={initiatingWithdrawal}
                    />
                  </div>
                  <div>
                    <label className={`text-[11px] ${styles.muted}`}>
                      Note (optional)
                    </label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="Reason for withdrawal"
                      value={adminWithdrawalNote}
                      onChange={(e) => setAdminWithdrawalNote(e.target.value)}
                      disabled={initiatingWithdrawal}
                    />
                  </div>
                </div>
                <p className={`text-[11px] mt-2 ${styles.muted}`}>
                  This will send an email to the user for approval. Amount will
                  only be deducted after user confirms.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${
                      initiatingWithdrawal
                        ? "bg-orange-400"
                        : "bg-orange-600 hover:bg-orange-500"
                    }`}
                    onClick={handleInitiateAdminWithdrawal}
                    disabled={initiatingWithdrawal || !adminWithdrawalAmount}
                  >
                    {initiatingWithdrawal
                      ? "Sending..."
                      : "Send Withdrawal Request"}
                  </button>
                </div>
              </div>
              {/* Transaction Report Section */}
              <div className={`p-3 rounded-lg ${styles.secondaryCard}`}>
                <p className={`text-xs ${styles.muted}`}>
                  Download Transaction Report
                </p>
                <div className="mt-2">
                  <label className={`text-[11px] ${styles.muted}`}>
                    Time Period
                  </label>
                  <select
                    className={`${styles.input} w-full`}
                    value={reportPeriod}
                    onChange={(e) => setReportPeriod(e.target.value)}
                    disabled={reportDownloading}
                  >
                    <option value="1day">Last 24 Hours</option>
                    <option value="7days">Last 7 Days</option>
                    <option value="30days">Last 30 Days</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                {reportPeriod === "custom" && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className={`text-[11px] ${styles.muted}`}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        className={styles.input}
                        value={reportCustomDates.start}
                        onChange={(e) =>
                          setReportCustomDates((prev) => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        disabled={reportDownloading}
                      />
                    </div>
                    <div>
                      <label className={`text-[11px] ${styles.muted}`}>
                        End Date
                      </label>
                      <input
                        type="date"
                        className={styles.input}
                        value={reportCustomDates.end}
                        onChange={(e) =>
                          setReportCustomDates((prev) => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        disabled={reportDownloading}
                      />
                    </div>
                  </div>
                )}
                <p className={`text-[11px] mt-2 ${styles.muted}`}>
                  Includes closed trades, deposits, withdrawals, and summary statistics.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${
                      reportDownloading
                        ? "bg-blue-400"
                        : "bg-blue-600 hover:bg-blue-500"
                    }`}
                    onClick={handleDownloadTransactionReport}
                    disabled={reportDownloading}
                  >
                    {reportDownloading ? "Downloading..." : "Download Report (CSV)"}
                  </button>
                </div>
              </div>
            </div>
            {selectedUser.username !== "admin" && (
              <div className="mb-4">
                <button
                  className="px-4 py-2 rounded bg-red-600 text-white text-sm"
                  onClick={() =>
                    handleDeleteUser(
                      selectedUser.user_id,
                      selectedUser.username
                    )
                  }
                >
                  Remove User
                </button>
              </div>
            )}

            {selectedPositions.length === 0 ? (
              <p className="opacity-70">No positions yet for this user.</p>
            ) : (
              <>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <label className={`text-xs ${styles.muted}`}>
                      Slippage (abs)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.input} w-28`}
                      value={exitAllSlippage}
                      onChange={(e) => setExitAllSlippage(e.target.value)}
                    />
                  </div>
                  <button
                    className={`rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white ${
                      exitAllLoading
                        ? "opacity-70 cursor-not-allowed"
                        : "hover:bg-red-500"
                    }`}
                    onClick={handleExitAllPositions}
                    disabled={exitAllLoading || !hasSpotCryptoPositions}
                  >
                    {exitAllLoading ? "Exiting..." : "Exit All Crypto"}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className={styles.tableClass}>
                    <thead className={styles.headerRow}>
                      <tr>
                        <th className={styles.cellClass}>Symbol</th>
                        <th className={styles.cellClass}>Side</th>
                        <th className={styles.cellClass}>Qty</th>
                        <th className={styles.cellClass}>Entry</th>
                        <th className={styles.cellClass}>Entry Time</th>
                        <th className={styles.cellClass}>TP</th>
                        <th className={styles.cellClass}>Slippage</th>
                        <th className={styles.cellClass}>Current</th>
                        <th className={styles.cellClass}>Status</th>
                        <th className={styles.cellClass}>Closed Price</th>
                        <th className={styles.cellClass}>P&L</th>
                        <th className={styles.cellClass}>Swap</th>
                        <th className={styles.cellClass}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPositions.map((position) => {
                        const edits =
                          positionEdits[selectedUser.user_id]?.[
                            position.position_id
                          ] || {};
                        const currentPriceTrendClass = getPriceTrendInputClass(
                          position,
                          edits
                        );
                        return (
                          <tr
                            key={position.position_id}
                            className="hover:bg-gray-700"
                          >
                            <td className={styles.cellClass}>
                              <input
                                className={styles.input}
                                value={edits.symbol ?? ""}
                                placeholder={
                                  position.display_symbol || position.symbol
                                }
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "symbol",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className={styles.cellClass}>
                              <select
                                className={styles.input}
                                value={edits.side ?? "buy"}
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "side",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="buy">Buy</option>
                                <option value="sell">Sell</option>
                              </select>
                            </td>
                            <td className={styles.cellClass}>
                              <input
                                type="number"
                                step="any"
                                className={styles.input}
                                value={edits.qty ?? ""}
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "qty",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className={styles.cellClass}>
                              <input
                                type="number"
                                step="any"
                                className={styles.input}
                                value={edits.entry_price ?? ""}
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "entry_price",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className={`${styles.cellClass} text-xs`}>
                              {position.open_time
                                ? formatTimestamp(position.open_time)
                                : position.opened_at
                                ? formatTimestamp(position.opened_at)
                                : position.created_at
                                ? formatTimestamp(position.created_at)
                                : "--"}
                            </td>
                            <td className={`${styles.cellClass} text-right`}>
                              {position.take_profit != null
                                ? Number(position.take_profit).toFixed(2)
                                : "--"}
                            </td>
                            <td className={`${styles.cellClass} text-right`}>
                              {position.entry_slippage != null
                                ? Number(position.entry_slippage).toFixed(2)
                                : "--"}
                            </td>
                            <td className={styles.cellClass}>
                              <input
                                type="number"
                                step="any"
                                className={`${styles.input} ${currentPriceTrendClass}`}
                                value={edits.current_price ?? ""}
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "current_price",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className={styles.cellClass}>
                              <select
                                className={styles.input}
                                value={
                                  edits.status ?? position.status ?? "open"
                                }
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "status",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="open">Open</option>
                                <option value="closed">Closed</option>
                              </select>
                            </td>
                            <td className={styles.cellClass}>
                              <input
                                type="number"
                                step="any"
                                className={styles.input}
                                value={edits.closed_price ?? ""}
                                placeholder={position.closed_price ?? ""}
                                onChange={(e) =>
                                  handleEditChange(
                                    selectedUser.user_id,
                                    position.position_id,
                                    "closed_price",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className={`${styles.cellClass} text-right`}>
                              {position.unrealized_pnl?.toFixed(2)}
                            </td>
                            <td className={`${styles.cellClass} text-right`}>
                              ${Number(position.swap_accrued || 0).toFixed(2)}
                            </td>
                            <td className={`${styles.cellClass} text-center`}>
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  className="bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-sm"
                                  onClick={() =>
                                    handleSavePosition(
                                      selectedUser.user_id,
                                      position.position_id
                                    )
                                  }
                                  disabled={
                                    loadingPositions ||
                                    closingPositionId === position.position_id
                                  }
                                >
                                  Save
                                </button>
                                <button
                                  className="bg-red-600 hover:bg-red-500 px-3 py-1 rounded text-sm text-white"
                                  onClick={() =>
                                    handleExitPosition(
                                      selectedUser.user_id,
                                      position.position_id
                                    )
                                  }
                                  disabled={
                                    closingPositionId === position.position_id
                                  }
                                >
                                  {closingPositionId === position.position_id
                                    ? "Closing..."
                                    : "Exit"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div
            style={{ display: SHOW_LEGACY_OPTION_CARDS ? undefined : "none" }}
            className={`mt-6 rounded-lg p-5 transition-colors duration-200 ${styles.secondaryCard}`}
          >
            <h3 className="text-xl font-semibold mb-4">
              Buy Option Position For {selectedUser.username}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <input
                className={styles.input}
                placeholder="Symbol (e.g. EURUSD)"
                value={newOption.symbol}
                onChange={(e) =>
                  handleNewOptionChange("symbol", e.target.value.toUpperCase())
                }
              />
              <input
                type="number"
                step="any"
                className={styles.input}
                placeholder="Quantity"
                value={newOption.qty}
                onChange={(e) => handleNewOptionChange("qty", e.target.value)}
              />
              <select
                className={styles.input}
                value={newOption.optionType}
                onChange={(e) =>
                  handleNewOptionChange("optionType", e.target.value)
                }
              >
                <option value="call">Call</option>
                <option value="put">Put</option>
              </select>
              <select
                className={styles.input}
                value={newOption.expiry}
                onChange={(e) =>
                  handleNewOptionChange("expiry", e.target.value)
                }
                disabled={loadingOptionExpiries || optionExpiries.length === 0}
              >
                <option value="">Select Expiry</option>
                {optionExpiries.map((expiry) => (
                  <option key={expiry} value={expiry}>
                    {expiry}
                  </option>
                ))}
              </select>
              <input
                type="number"
                step="any"
                className={styles.input}
                placeholder="Strike Price"
                value={newOption.strike}
                onChange={(e) =>
                  handleNewOptionChange("strike", e.target.value)
                }
                disabled={!newOption.expiry}
              />
              <input
                type="number"
                step="any"
                className={styles.input}
                placeholder="Premium (Price)"
                value={newOption.premium}
                onChange={(e) =>
                  handleNewOptionChange("premium", e.target.value)
                }
              />
              <input
                type="number"
                step="any"
                className={styles.input}
                placeholder="Current Price (optional)"
                value={newOption.current}
                onChange={(e) =>
                  handleNewOptionChange("current", e.target.value)
                }
              />
            </div>
            <div className={`mt-3 text-sm ${styles.muted} flex flex-col gap-1`}>
              {loadingOptionExpiries ||
              loadingOptionStrikes ||
              loadingOptionPrice ? (
                <span>Fetching option data...</span>
              ) : (
                <>
                  {optionSpot !== null && (
                    <span>GLD Spot: ${optionSpot.toFixed(2)}</span>
                  )}
                  <span>
                    {optionPrice !== null
                      ? `Live price for selected strike: $${optionPrice.toFixed(
                          2
                        )}`
                      : "Select expiry and enter a strike to view live premium."}
                  </span>
                </>
              )}
            </div>
            <button
              className="mt-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded"
              onClick={handleCreateOption}
              disabled={creatingOption}
            >
              {creatingOption ? "Placing Option..." : "Create Option Position"}
            </button>
          </div>

          {/* ────────────────────────────────────────────────────────────
              S&P500 OPTIONS MANUAL TRADE PANEL (User-specific)
              ──────────────────────────────────────────────────────────── */}
          <div style={{ display: SHOW_LEGACY_OPTION_CARDS ? undefined : "none" }} className={`mt-6 p-4 rounded-lg border ${styles.borderMuted} ${styles.secondaryCard}`}>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setSpxPanelOpen(!spxPanelOpen)}
            >
              <div>
                <h3 className="text-lg font-semibold">S&P500 Options Trade</h3>
                <p className={`text-xs ${styles.muted}`}>
                  Place multi-leg options (CE/PE) for {selectedUser.username}
            </p>
          </div>
          <span className="text-xl">{spxPanelOpen ? "▼" : "▶"}</span>
        </div>

        {spxPanelOpen && (
          <div className="mt-4 space-y-4">
            {/* Apply to toggle */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={spxApplyAll}
                  onChange={(e) => { setSpxApplyAll(e.target.checked); setSpxPreview(null); }}
                  className="w-4 h-4"
                />
                Apply to ALL standard users (19)
              </label>
              {!spxApplyAll && (
                <span className={`text-xs px-2 py-0.5 rounded bg-blue-600 text-white`}>
                  {selectedUser.username} only (×{spxMultipliers[selectedUser.user_id] || 1.0})
                </span>
              )}
            </div>

            {/* Single-user multiplier */}
            {!spxApplyAll && (
              <div className="flex items-center gap-2 text-sm">
                <span className={styles.muted}>Multiplier for {selectedUser.username}:</span>
                <input
                  type="number" min="0.1" step="0.1"
                  className={`${styles.input} w-20 text-center`}
                  value={spxMultipliers[selectedUser.user_id] || 1.0}
                  onChange={(e) => setSpxMultipliers((prev) => ({ ...prev, [selectedUser.user_id]: parseFloat(e.target.value) || 1.0 }))}
                />
                <span className={`text-xs ${styles.muted}`}>
                  Eff. Qty: {Math.floor((parseFloat(spxLegs.find(l => l.enabled)?.qty_lots) || 100) * (spxMultipliers[selectedUser.user_id] || 1.0))} contracts
                </span>
                <button
                  className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded"
                  onClick={handleSaveMultipliers}
                  disabled={savingMultipliers}
                >{savingMultipliers ? "Saving..." : "Save"}</button>
              </div>
            )}

            {/* User Multipliers table (when Apply to ALL) */}
            {spxApplyAll && (
              <div className={`p-3 rounded-lg border ${styles.borderMuted} ${styles.secondaryCard}`}>
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setSpxMultipliersOpen(!spxMultipliersOpen)}
                >
                  <span className="text-sm font-semibold">User Multipliers</span>
                  <span className="text-sm">{spxMultipliersOpen ? "▼" : "▶"}</span>
                </div>
                {spxMultipliersOpen && (
                  <div className="mt-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={`border-b ${styles.borderMuted}`}>
                          <th className="text-left py-1 px-1">Username</th>
                          <th className="text-center py-1 px-1 w-20">Multiplier</th>
                          <th className="text-right py-1 px-1">Eff. Qty</th>
                          <th className="text-right py-1 px-1">Est. PNL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users
                          .filter((u) => [
                            "Abheekjindal","ajayymgoyal","Amitbatra","Ankur mehta","ANSHU2568MAN",
                            "dheeraaj","Gaurav Dawar","great4141","Gurjant_uppal","ijindal",
                            "Karanjindal","Kumar Anurag","log101","rakesh","Rakshit",
                            "rastadi89","Sanjeevjindal","Sharma5Ankush","Shelly"
                          ].includes(u.username))
                          .sort((a, b) => a.username.localeCompare(b.username))
                          .map((u) => {
                            const mult = spxMultipliers[u.user_id] || 1.0;
                            const baseQty = parseFloat(spxLegs.find(l => l.enabled)?.qty_lots) || 100;
                            const effQty = Math.floor(baseQty * mult);
                            const userPnl = spxLegs.reduce((s, l) => s + calculateLegPnl(l, mult), 0);
                            return (
                              <tr key={u.user_id} className={`border-b ${styles.borderMuted}`}>
                                <td className="py-1 px-1">{u.username}</td>
                                <td className="py-1 px-1 text-center">
                                  <input
                                    type="number" min="0.1" step="0.1"
                                    className={`${styles.input} w-16 text-center text-xs`}
                                    value={mult}
                                    onChange={(e) => setSpxMultipliers((prev) => ({ ...prev, [u.user_id]: parseFloat(e.target.value) || 1.0 }))}
                                  />
                                </td>
                                <td className="py-1 px-1 text-right">{effQty}</td>
                                <td className={`py-1 px-1 text-right ${userPnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                                  ${userPnl.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    <button
                      className="mt-2 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-semibold"
                      onClick={handleSaveMultipliers}
                      disabled={savingMultipliers}
                    >{savingMultipliers ? "Saving..." : "Save All Multipliers"}</button>
                  </div>
                )}
              </div>
            )}

            {/* Date inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-xs font-semibold block mb-1 ${styles.muted}`}>Trade Date</label>
                <input
                  type="date"
                  className={styles.input}
                  value={spxTradeDate}
                  onChange={(e) => { setSpxTradeDate(e.target.value); setSpxPreview(null); }}
                />
              </div>
              <div>
                <label className={`text-xs font-semibold block mb-1 ${styles.muted}`}>Expiry Date</label>
                <input
                  type="date"
                  className={styles.input}
                  value={spxExpiryDate}
                  onChange={(e) => { setSpxExpiryDate(e.target.value); setSpxPreview(null); }}
                />
              </div>
            </div>

            {/* 4 Leg Cards */}
            {spxLegs.map((leg, idx) => {
              const legPnl = calculateLegPnl(leg);
              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${leg.enabled ? styles.borderMuted : "border-gray-600 opacity-50"} ${styles.secondaryCard}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={leg.enabled}
                      onChange={(e) => updateSpxLeg(idx, "enabled", e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="font-bold text-sm">Leg {idx + 1}: {LEG_LABELS[idx]}</span>
                    {leg.enabled && leg.entry_premium !== "" && leg.exit_premium !== "" && (
                      <span className={`text-xs font-mono ml-auto ${legPnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        PNL: ${legPnl.toFixed(2)}
                      </span>
                    )}
                  </div>
                  {leg.enabled && (
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className={`text-[11px] block mb-0.5 ${styles.muted}`}>Strike Price</label>
                        <input type="number" step="any" className={styles.input} placeholder="e.g. 5200"
                          value={leg.strike_price} onChange={(e) => updateSpxLeg(idx, "strike_price", e.target.value)} />
                      </div>
                      <div>
                        <label className={`text-[11px] block mb-0.5 ${styles.muted}`}>Entry Premium</label>
                        <input type="number" step="any" className={styles.input} placeholder="e.g. 12.50"
                          value={leg.entry_premium} onChange={(e) => updateSpxLeg(idx, "entry_premium", e.target.value)} />
                      </div>
                      <div>
                        <label className={`text-[11px] block mb-0.5 ${styles.muted}`}>Exit Premium</label>
                        <input type="number" step="any" className={styles.input} placeholder="e.g. 18.30"
                          value={leg.exit_premium} onChange={(e) => updateSpxLeg(idx, "exit_premium", e.target.value)} />
                      </div>
                      <div>
                        <label className={`text-[11px] block mb-0.5 ${styles.muted}`}>Qty (Contracts)</label>
                        <input type="number" min="1" step="1" className={styles.input}
                          value={leg.qty_lots} onChange={(e) => updateSpxLeg(idx, "qty_lots", e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Notes */}
            <div>
              <label className={`text-xs font-semibold block mb-1 ${styles.muted}`}>Notes (optional)</label>
              <input type="text" className={styles.input} placeholder="e.g. Bull call spread 5200/5250"
                value={spxNotes} onChange={(e) => setSpxNotes(e.target.value)} />
            </div>

            {/* Summary */}
            {spxEnabledCount > 0 && (
              <div className={`text-sm font-semibold ${spxTotalPreviewPnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                {spxEnabledCount} leg(s) — PNL per user: ${spxTotalPreviewPnl.toFixed(2)}
                {spxApplyAll ? " (× all standard users)" : ` (${selectedUser.username} only)`}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded text-sm font-semibold"
                onClick={handleSpxPreview} disabled={spxLoading}>Preview</button>
              <button className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                onClick={handleSpxExecute} disabled={spxLoading || !spxPreview}>
                {spxLoading ? "Executing..." : "Execute Trades"}</button>
              <button className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-2 rounded text-sm"
                onClick={resetSpxPanel} disabled={spxLoading}>Reset</button>
            </div>

            {/* Preview Table */}
            {spxPreview && (
              <div className="mt-3">
                <p className="text-xs font-semibold mb-2 uppercase opacity-60">Preview — Per User Breakdown</p>
                <table className={styles.tableClass}>
                  <thead>
                    <tr className={styles.headerRow}>
                      <th className={styles.cellClass}>Leg</th>
                      <th className={styles.cellClass}>Type</th>
                      <th className={styles.cellClass}>Strike</th>
                      <th className={styles.cellClass}>Entry Prem</th>
                      <th className={styles.cellClass}>Exit Prem</th>
                      <th className={styles.cellClass}>Lots</th>
                      <th className={styles.cellClass}>Contracts</th>
                      <th className={styles.cellClass}>PNL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spxPreview.legs.map((l, i) => (
                      <tr key={i}>
                        <td className={styles.cellClass}>{i + 1}</td>
                        <td className={styles.cellClass}>{l.label}</td>
                        <td className={styles.cellClass}>{l.strike}</td>
                        <td className={styles.cellClass}>${l.entry.toFixed(2)}</td>
                        <td className={styles.cellClass}>${l.exit.toFixed(2)}</td>
                        <td className={styles.cellClass}>{l.lots}</td>
                        <td className={styles.cellClass}>{l.contracts}</td>
                        <td className={`${styles.cellClass} font-semibold ${l.pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                          ${l.pnl.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className={styles.headerRow}>
                      <td colSpan={7} className={`${styles.cellClass} font-bold text-right`}>Total PNL per user:</td>
                      <td className={`${styles.cellClass} font-bold ${spxPreview.totalPnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        ${spxPreview.totalPnl.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Execution Results */}
            {spxResult && spxResult.success && (
              <div className="mt-3">
                <p className="text-xs font-semibold mb-2 uppercase opacity-60">Execution Results</p>
                <div className={`text-sm mb-2 p-2 rounded ${styles.statusSuccess}`}>
                  ✅ {spxResult.trades_created} trades for {spxResult.users_affected} user(s) | Total PNL: ${spxResult.total_pnl}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className={styles.tableClass}>
                    <thead>
                      <tr className={styles.headerRow}>
                        <th className={styles.cellClass}>User</th>
                        <th className={styles.cellClass}>Legs</th>
                        <th className={styles.cellClass}>Total PNL</th>
                        <th className={styles.cellClass}>New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spxResult.per_user.map((u) => (
                        <tr key={u.user_id}>
                          <td className={styles.cellClass}>{u.username}</td>
                          <td className={styles.cellClass}>
                            {u.legs.map((l) => `${l.type} @${l.strike} $${l.pnl}`).join(" | ")}</td>
                          <td className={`${styles.cellClass} font-semibold ${u.total_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                            ${u.total_pnl.toFixed(2)}</td>
                          <td className={styles.cellClass}>${u.new_balance.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

    </div>
  );
}
